import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { HttpErrorResponse } from '@angular/common/http'
import {
  CellClassParams,
  CellClickedEvent,
  CellStyle,
  ColDef,
  ColumnApi,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  Module,
  RowClassParams,
  RowDoubleClickedEvent,
  RowNode,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { SetFilterModule } from '@ag-grid-enterprise/set-filter'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  getFilterOnCellContextMenu,
  getSelectedForContextMenu,
  heliosClientSideRowConfig,
  heliosMultiRowSelectionConfig,
} from '@helios/ag-grid-config'
import {
  AggInfoFilterModalService,
  ColumnTypeOverride,
  ColumnTypeOverrideService,
  FilterModalService,
} from '@helios/filter-modal'
import {
  EventType,
  FlexRowUpdateEvent,
  HeliosMessageType,
  RowIdPayload,
  SessionMessagingService,
} from '@helios/messaging'
import {
  createPanelSettingsToolPanel,
  HighlightingService,
} from '@helios/generic-components'
import {
  createFilterGroupsToolPanel,
  DensityLevel,
  FilterGroupsToolPanelParams,
  UserPreferencesService,
} from '@helios/user-preferences'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ToastService } from '@northfork/ng-basics'
import { EMPTY, merge, Observable, of, Subject } from 'rxjs'
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  skip,
  switchMap,
  takeUntil,
} from 'rxjs/operators'
import { WorkbenchGridContext } from '../../models/grid-context.model'
import {
  AGGREGATE_INFO_GROUP,
  ColumnCatalogEntry,
  SourceDefinition,
} from '../../models/source-definition.model'
import {
  GridRole,
  MatchTargetKey,
  RowOrigin,
  RPS_SOURCE,
  SourceKey,
  TARGET_META,
} from '../../models/source-key.model'
import { RowStatus, WorkbenchRow } from '../../models/workbench-row.model'
import {
  isSourceSelectable,
  SOURCE_REGISTRY,
} from '../../registry/source-registry'
import {
  buildColDefs,
  colSourceMap,
  joinList,
  STATIC_UNSORTABLE_FIELDS,
} from '../../grid/col-def-factory'
import { buildFieldMeta } from '../../grid/query/field-types'
import {
  AGG_INFO_FILTER,
  AggInfoColumnFilterComponent,
} from '../../grid/filters/agg-info-column-filter.component'
import {
  buildRequestFilter,
  buildSort,
  GridSortModelEntry,
  RequestFilterInput,
  requestSnapshot,
  serializeRequest,
} from '../../grid/filters/request-filter.builder'
import { QuerySort, RequestFilter } from '../../models/transactions-query.model'
import {
  classifyQueryError,
  invalidFieldTypeMessage,
  QueryFailure,
} from '../../services/query-error'
import {
  buildComputedColumns,
  MATCH_FIELD_TARGETS,
} from '../../registry/computed-columns'
import { AdaptedCatalog, adaptCatalog } from '../../grid/field-catalog.adapter'
import { FieldCatalogService } from '../../services/field-catalog.service'
import { WorkbenchDataSource } from '../../services/workbench-data-source'
import { createWorkbenchColumnManagerToolPanel } from '../column-manager/workbench-column-manager.sidebar-utils'
import {
  externalFilterActive,
  passesExternalFilter,
  QuickFilterState,
  StatusFilterState,
  WorkbenchExternalFilterModel,
} from '../../grid/filters/external-filters'
import { serverMatchStatusValues } from '../../grid/filters/match-status-filter'
import {
  SEARCH_FIELD,
  SEARCH_PLACEHOLDER,
  SearchConstraint,
} from '../../grid/filters/search-filter'
import {
  workbenchStatusBarPanel,
  WorkbenchStatusBarModule,
} from '../../grid/status-bar/workbench-status-bar.module'
import { Transactions2Key } from '../../../panel-key'
import { AggregateInfoService } from '../../services/aggregate-info.service'
import {
  MatchingService,
  MatchStateSnapshot,
} from '../../services/matching.service'
import { WorkbenchStoreService } from '../../services/workbench-store.service'
import { TransactionActionsService } from '../../services/transaction-actions.service'
import {
  ActionIdSets,
  buildFlexTransactionActionRequest,
  FLEX_ACTION_SUCCESS_NOTE,
  FLEX_ACTION_VALIDATION_COPY,
  FlexTransactionActionType,
} from '../../models/transaction-action.model'
import { hasServerRowId, mapRowsToActionIds } from '../../utils/action-ids'
import { readValidations } from '../../utils/read-validations'
import {
  buildContextMenuItems,
  POST_ENFUSION_LABEL,
} from '../../utils/context-menu'
import {
  UnmatchModalComponent,
  UnmatchModalResult,
  UnmatchSourceOption,
} from '../unmatch-modal/unmatch-modal.component'
import { BulkTransactionModalComponent } from '../bulk-transaction-modal/bulk-transaction-modal.component'
import { EditTransactionModalComponent } from '../edit-transaction-modal/edit-transaction-modal.component'
import {
  BulkDerivations,
  bulkOpeningDerivations,
  BulkTransactionEntry,
  BulkTransactionMode,
  BulkTransactionViewModel,
  projectBulkTransaction,
} from '../../bulk-transaction/bulk-transaction.projection'
import {
  accountIdOf,
  EditTransactionMode,
  projectEditTransaction,
} from '../../edit-transaction/edit-transaction.projection'
import { HttpGlEntryReadService } from '../../services/http-gl-entry-read.service'
import { HttpTradeReadService } from '../../services/http-trade-read.service'
import { HttpTransactionMatchesService } from '../../services/http-transaction-matches.service'
import {
  DerivationRequest,
  DerivationResult,
  TransactionDerivationSource,
} from '../../services/transaction-derivation.service'
import { USE_HTTP_TRANSACTION_DERIVATION } from '../../services/http-transaction-derivation.service'

/**
 * The toast when the Edit dialog's `transactions/matches` read fails (PVAL-26451). The dialog still
 * opens; only its Custody/Trading comparison is missing.
 */
const MATCHES_READ_ERROR =
  'Matched Custody/Trading transactions could not be loaded; the comparison columns are empty'

/**
 * `primary` first, then every `secondary` entry whose `(origin, rowId)` is not already present —
 * so a counterpart the server returned and the session also knows appears once, as the server's.
 */
const mergeCounterparts = (
  primary: readonly WorkbenchRow[],
  secondary: readonly WorkbenchRow[],
): WorkbenchRow[] => {
  const keyOf = (entry: WorkbenchRow) => `${entry.origin}\u0000${entry.rowId}`
  const seen = new Set(primary.map(keyOf))
  return [
    ...primary,
    ...secondary.filter(entry => {
      const key = keyOf(entry)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    }),
  ]
}

/**
 * The heading on the dialogs' error box when a trade read fails.
 *
 * Says which read failed, not what went wrong — the detail underneath is RPS's own rejection body,
 * and a header that guessed at the cause would contradict it.
 */
const TRADE_READ_ERROR_HEADER = 'Error loading transaction details'
import {
  TranCategoryResolution,
  resolveTranCategory,
} from '../../edit-transaction/tran-category'
import {
  CreateMenuItem,
  TranCategoryKey,
} from '../../models/transaction-metadata.model'
import { HttpDialogListValuesService } from '../../services/http-dialog-list-values.service'
import { TransactionMetadataSource } from '../../services/transaction-metadata.service'
import { TransactionValueSource } from '../../services/transaction-value.service'

/** A row's id for a record read, trimmed; empty when the row does not carry it. */
const idText = (raw: unknown): string => (raw == null ? '' : String(raw).trim())

/** What a record read answered, whichever endpoint answered it. */
interface RecordRead {
  /** The whole documents, keyed by the id the read was asked for. */
  documents: ReadonlyMap<string, Record<string, unknown>>
  error: unknown | null
}

/**
 * One of the reads that fill a dialog with the whole record behind a grid row (PVAL-26492 for a
 * trade, PVAL-26685 for a GL journal entry).
 *
 * The grid row carries only the columns the workbench asked for; the dialog needs every key the
 * metadata declares. Which endpoint holds the rest depends on what the row IS, and the row says so
 * by the id it carries: `gLEntryId` for a journal entry, `tradeId` for a trade. Keying on the id
 * rather than on the tran category keeps this free of any one client's category vocabulary.
 */
interface RecordSource {
  /** The row field holding the id this read is keyed on. */
  idField: string
  /** `tranCategory` only matters to a trade read. */
  read(
    ids: readonly string[],
    accountId: string,
    tranCategory?: string,
  ): Observable<RecordRead>
}

interface SourceOption {
  key: SourceKey
  label: string
  /** Short transport/model tag shown beside the label (EXT / CORE / API). */
  tag: string
  /** Accent for the tag — blue for the Helios-side feeds, purple for the Enfusion API. */
  tagColor: string
  /** Listed but not choosable — a pairing the workbench is not cleared to show yet. */
  disabled: boolean
}

/**
 * Fallback when there is nothing to adapt — no focused account, or the fetch failed. The grid still
 * renders its computed columns, and the Column Manager recovers their Helios section from the
 * columns themselves.
 */
const EMPTY_CATALOG: AdaptedCatalog = { columns: [], groupOrder: [] }

/** The Helios ReportableError (StandardApiError) envelope, of which we only need a few fields here.
 * Defined locally to avoid a cross-lib dependency on @helios/messaging just for these. */
interface ReportableErrorEnvelope {
  errorId?: string
  displayMessage?: string
  errorMessage?: string
}

/**
 * Verbs whose optimistic interim apply is a flat status rather than a change of match membership,
 * and the status each one shows.
 *
 * Carrying them in a table is what keeps `submitAction`'s dispatch honest: its match/unmatch
 * if/else ENDS in `applyOptimisticMatch`, so a verb that simply fell through would render the
 * actioned rows as MATCHED — and, below two rows, as nothing at all, because that method
 * early-returns. Membership of this table is also what suppresses the reconcile refetch
 * (see {@link WorkbenchGridComponent.submitAction}).
 */
const OPTIMISTIC_STATUS: Partial<Record<FlexTransactionActionType, RowStatus>> =
  {
    [FlexTransactionActionType.hold]: 'held',
    [FlexTransactionActionType.ignore]: 'ignored',
  }

/**
 * The flex row-update events this grid listens for. Both are subscribed and then filtered by the
 * current source via {@link FLEX_UPDATE_EVENT_FOR_SOURCE}, so a source switch needs no re-subscribe.
 */
const FLEX_ROW_UPDATE_EVENTS: readonly HeliosMessageType[] = [
  HeliosMessageType.FlexTransactionUpdate,
  HeliosMessageType.FlexExternalTransactionUpdate,
]

/**
 * Which row-update event belongs to each grid source. `flex-transaction/update` is the Enfusion
 * (`transactions`) feed and `flex-external-transaction/update` the Prometheus
 * (`external_transactions`) feed. Clearwater is not a flex feed and has no such event — and cannot
 * be a flex match member at all — so a CW grid never refreshes from one.
 */
const FLEX_UPDATE_EVENT_FOR_SOURCE: Record<
  SourceKey,
  HeliosMessageType | undefined
> = {
  api: HeliosMessageType.FlexTransactionUpdate,
  prom: HeliosMessageType.FlexExternalTransactionUpdate,
  cw: undefined,
}

/**
 * Pull the `errorId` out of a failed catalog request. Helios API errors carry a StandardApiError
 * envelope on `HttpErrorResponse.error`; bug reports are keyed on its `errorId`, so it is surfaced
 * on both catalog-error states. Returns null when the failure carried no such envelope (e.g. a
 * transport-level failure with no response body).
 */
const extractErrorId = (err: unknown): string | null => {
  if (!(err instanceof HttpErrorResponse)) return null
  const envelope = err.error as ReportableErrorEnvelope | null | undefined
  return envelope?.errorId ?? null
}

/** One item in a source's Create menu. */
/** Clearwater posting types offered by the Create menu, in the order the 2.0 design lists them. */
const CREATE_TYPES: readonly string[] = [
  'Cash Entry',
  'Trade',
  'Fx Spot Trade',
  'Currency Forward',
  'Wire Transfer',
  'Direct Transfer',
  'Security Transfer',
]

/**
 * The source-agnostic grid. One instance per role (Comparison / Base); it reads its current source
 * from the shared {@link WorkbenchStoreService}, builds column defs from that source's catalog, and
 * routes selection / matching / cross-filter through the store. All cross-grid coordination happens
 * via the store — this component never references its sibling.
 */
@Component({
  selector: 'hs-workbench-grid',
  templateUrl: './workbench-grid.component.html',
  styleUrls: ['./workbench-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // FilterModalService is deliberately grid-scoped — each of the two workbench grids registers its
  // own ag-Grid instance with it.
  //
  // The AggInfo subclass (PVAL-26423) is what turns on the column-type picker and the override-aware
  // column models (`getAggInfoAwareColumnFilterModels`), so a Number or Date condition chosen in the
  // modal is recorded against the column instead of being built as text. Without it, every Aggregate
  // Info column is offered as a plain text filter and §4 does not work at all. Provided under the base
  // token so nothing else in the panel has to know which implementation it got.
  providers: [
    HighlightingService,
    { provide: FilterModalService, useClass: AggInfoFilterModalService },
  ],
})
export class WorkbenchGridComponent implements OnInit, OnDestroy {
  /**
   * Attempts made to see a successful action land before the grid stops waiting on it.
   *
   * Part of the interim seam (PVAL-26130 CHANGE 6): each attempt is a full page reload (`PAGE_SIZE` rows), so this
   * is a bounded re-check, NOT a poll. The permanent answer is the backend's match-changed event.
   */
  private static readonly RECONCILE_ATTEMPTS = 2

  /** Delay before each reconcile attempt. */
  private static readonly RECONCILE_DELAY_MS = 1500

  /**
   * How long to wait before acting on a filter / sort change.
   *
   * Long enough to collapse a burst (typing into a filter value, multi-sorting several columns), short
   * enough that a single deliberate change still feels immediate.
   */
  private static readonly REFETCH_DEBOUNCE_MS = 300

  @Input() role!: GridRole

  private readonly destroy$ = new Subject<void>()

  /** Pushes when the user clicks Retry on a retryable (5xx) catalog error — re-drives the fetch. */
  private readonly retry$ = new Subject<void>()

  /**
   * Pushes when this grid's rows must be reloaded WITHOUT rebuilding the column catalog — a match
   * changed somewhere, so the row data is stale but the column set is not. Kept separate from
   * {@link retry$} on purpose: `retry$` is merged into the catalog pipeline, and re-pulling the
   * field catalog on every match would be a wasteful request per action.
   */
  private readonly rowsRefresh$ = new Subject<void>()

  /**
   * Pushes when the SERVER-side predicate has changed — a column filter or a sort — and the page has
   * to be re-fetched (PVAL-26129 §5/§7).
   *
   * Kept separate from {@link rowsRefresh$} because the two are driven by different things and only
   * this one needs debouncing: a user dragging through filter values or multi-sorting three columns
   * generates a burst, and `switchMap` plus a debounce collapses the burst into the one request that
   * describes where they landed.
   */
  private readonly refetch$ = new Subject<void>()

  /**
   * The ONE stream every page-replacing row request goes through: the first page after a catalog
   * resolves, a rows-only refresh, and a filter / sort refetch. A single `switchMap` over all three
   * is what makes "newest request wins" true ACROSS drivers — with one stream each, a slow unfiltered
   * first page could land after the filtered page the user asked for and paint over it, while the
   * snapshot claims the screen is filtered.
   *
   * The payload is that `'replace'` rebuilds the ColDefs (Aggregate Info columns are discovered from
   * the rows) and `'preserve'` keeps the user's filter model and selection; see
   * {@link pendingRowsReplace} for a replace that is superseded before it lands.
   */
  private readonly rowsRequest$ = new Subject<'replace' | 'preserve'>()

  /**
   * Set while a `'replace'` request is outstanding, so that if a `'preserve'` request supersedes it
   * the page that finally lands is still applied as a replace — otherwise the ColDef rebuild the
   * first page owes would be silently skipped. Cleared when a page is applied.
   */
  private pendingRowsReplace = false

  /**
   * The serialized filter + sort payload the CURRENT page was fetched with, or null before any fetch.
   *
   * This is the loop guard. `applyRows()` calls `gridApi.onFilterChanged()`, and so do the status
   * chips, the quick filters and the cross-filter — so "ag-Grid says the filter changed" is not
   * evidence the server-side predicate changed. Stamped by {@link loadRows} at the moment it builds a
   * payload, compared by {@link maybeRefetch}; a difference is the only thing that issues a request.
   *
   * Cleared on a source or account switch so a snapshot describing the OLD source's catalog can never
   * suppress the new source's first fetch.
   */
  private requestSnapshotValue: string | null = null

  /**
   * The classified failure from the last /query call, or null.
   *
   * Every failure raises an error toast (see `presentRowsFailure`). Only `'invalid-field-type'` adds
   * an in-grid strip, because it is actionable per column: Reset to Text. A 500 from an un-castable
   * dynamic value is toast-only until the backend ports `asFieldTypeError` (§6).
   */
  queryFailure: QueryFailure | null = null

  readonly gridModules: Module[] = [
    MenuModule,
    SetFilterModule,
    RangeSelectionModule,
    FiltersToolPanelModule,
    WorkbenchStatusBarModule,
  ]

  gridApi?: GridApi
  /** Needed for the sort model: ag-Grid 26 exposes sort state through the COLUMN api, not the grid
   * api — `getColumnState()` carries `sort` + `sortIndex`, which is where precedence order lives. */
  columnApi?: ColumnApi
  gridOptions?: GridOptions

  sourceKey!: SourceKey
  source!: SourceDefinition
  roleLabel = ''
  sourceOptions: SourceOption[] = []
  /** The current source's Create-menu items; recomputed with the rest of the source metadata. */
  createMenuItems: readonly CreateMenuItem[] = []
  /** Whether the Create menu's "Trade" flyout is open. Hover-driven, as in the prototype; reset
   * whenever the Create menu itself opens or closes. */
  createTradeSubmenuOpen = false

  // Toolbar. Reactive controls throughout so the Northfork form components (nf-checkbox, ng-select,
  // nf-form-control) are driven the same way as every other Helios grid header.
  readonly quickSearch = new FormControl('')
  readonly includeUnmatchedFormControl = new FormControl(true)
  readonly includeIgnoredFormControl = new FormControl(false)
  readonly includeMatchedFormControl = new FormControl(false)
  readonly hideFuturePostDatesToggle = new FormControl(false)
  readonly filterPostDateTodayToggle = new FormControl(false)
  readonly filterClearedToggle = new FormControl(false)
  readonly filterUnclearedToggle = new FormControl(false)
  readonly fxCurrencyFilter = new FormControl([])
  fxCurrencyFilterItems: string[] = []

  // Panel settings, surfaced through the Panel Settings tool panel like every other Helios grid.
  // Clear Filters on Load is the only one this panel offers.
  readonly clearFiltersOnAccountLoadToggle = new FormControl(true)

  // Header filter affordances, mirroring the classic transaction grids.
  filterCount = 0
  clearAllFiltersEnabled = false

  private relatedRowIds = new Set<string>()
  private crossFilterIds: string[] | null = null
  private colDefs: ColDef[] = []
  private catalogColumns: ColumnCatalogEntry[] = []
  /** Column Manager section order for the current source, straight from the catalog's groups. */
  private catalogGroupOrder: string[] = []

  /**
   * The reads that prefill a dialog, tried in order: a row with a GL entry id is a journal entry,
   * else a row with a trade id is a trade. GL first because the tran category rule does the same —
   * every trade category requires `isNull gLEntryId`, so a row carrying one opens the GL Entry
   * dialog whatever else it carries, and has to be read as one.
   */
  private readonly recordSources: readonly RecordSource[] = [
    {
      idField: 'gLEntryId',
      // Looked up on use rather than injected, the way `TransactionDerivationSource` is: only a
      // journal-entry dialog needs it, and the constructor stays as it is.
      read: (ids, accountId) =>
        this.injector
          .get(HttpGlEntryReadService)
          .loadMany(ids, accountId)
          .pipe(map(read => ({ documents: read.entries, error: read.error }))),
    },
    {
      idField: 'tradeId',
      read: (ids, accountId, tranCategory) =>
        this.tradeRead
          .loadMany(ids, accountId, tranCategory)
          .pipe(map(read => ({ documents: read.trades, error: read.error }))),
    },
  ]

  /** Column-pipeline state, surfaced to the template so the grid can show loading / error /
   * empty affordances while the field catalog is fetched. */
  columnsLoading = false
  /**
   * The kind of catalog-fetch failure, split per item 10 so the two failure modes read differently:
   *  - `'not-retryable'`: a 404 — this account's CW client has no Enfusion metadata. Retrying the
   *    same request will keep 404ing, so no retry affordance is offered; the sibling grid (e.g.
   *    Prometheus) still renders.
   *  - `'retryable'`: a 5xx (or transport failure) — a transient server-side problem, so the user
   *    is offered a Retry that refetches this grid's catalog.
   *  - `'none'`: no error.
   */
  columnsErrorKind: 'none' | 'not-retryable' | 'retryable' = 'none'
  /**
   * The `errorId` from the Helios ReportableError (StandardApiError) envelope, surfaced on BOTH
   * error states because bug reports are keyed on it. Null when the response carried no envelope.
   */
  columnsErrorId: string | null = null
  columnsEmpty = false

  /** Back-compat convenience: true whenever the catalog fetch failed, either kind. */
  get columnsError(): boolean {
    return this.columnsErrorKind !== 'none'
  }

  /** Row-pipeline state. Rows are fetched after the catalog, so this trails the column flags. */
  rowsLoading = false

  /**
   * The server's own message from the last failed Match / Unmatch, rendered in-panel.
   *
   * `HANDLE_ERROR_AND_THROW` hands the `HttpErrorResponse` to us precisely so this can be shown
   * verbatim: the endpoint returns a distinguishable message per validation case, and paraphrasing
   * it would lose which rule was broken.
   *
   * Whether the global error drawer *also* records the failure is a separate question, answered by
   * `WORKFLOW_ERROR_STATUSES` in `http-transaction-actions.service.ts`: a 400/409 comes here
   * *instead of* the drawer, because routine workflow validation is not a defect; any other status
   * (5xx, or a status-0 transport failure) comes here *in addition to* it, which is intended — that
   * is a defect, and the drawer is where its errorId is kept for a bug report.
   */
  actionError: string | null = null

  /**
   * Disclosure for an action the server ACCEPTED, from {@link FLEX_ACTION_SUCCESS_NOTE}.
   *
   * Separate from {@link actionError} and from {@link actionPending} because it is neither: the
   * action worked, and nothing is outstanding. It exists because Hold / Ignore succeed into a state
   * this panel cannot read back or undo, and the success path is where the user needs to be told
   * that. Null for every verb with no note — match / unmatch report themselves through the
   * reconcile.
   */
  actionNote: string | null = null

  /**
   * Set when an optimistic apply was reverted because the refetch did not agree with it — the action
   * may still land later (the write path is asynchronous), so the user is told rather than left
   * looking at a silently reverted grid.
   */
  actionPending = false

  /**
   * Whether the pending action is one that CANNOT become visible until the next Run, as opposed to
   * one that is merely slow.
   *
   * A brand-new match group is exactly that case, by backend design: `FLEX_MATCH` carries no group
   * id (perseus mints it), so portfolio has no handler for it and writes nothing to `flex_matches`
   * in-session. `FLEX_ADD_MATCH` and `FLEX_UNMATCH` both name an existing group and do apply
   * immediately. Telling the user "the grid will update when it is" for a create would be a promise
   * this session cannot keep.
   */
  actionPendingNeedsRun = false

  /** Mutated in place — the status panels and column comparators read it through
   * `gridOptions.context`. */
  private readonly gridContext: WorkbenchGridContext = {
    rangeSumEnabled: false,
    sortByAbsolute: false,
  }

  /**
   * Filter Groups tool panel wiring. Scoped per role so the two grids keep separate saved groups,
   * matching how this grid already registers with the Filter Modal. The external-filter accessors
   * are inert, as they are on the other Helios grids that expose this panel.
   */
  get filterGroupsToolPanelParams(): FilterGroupsToolPanelParams<unknown> {
    return {
      gridKey: `${Transactions2Key}-${this.role}Grid`,
      getExternalFilters: () => ({}),
      setExternalFilters: () => undefined,
      cacheLoaded: of(true),
    }
  }

  constructor(
    private readonly store: WorkbenchStoreService,
    private readonly matching: MatchingService,
    private readonly aggInfo: AggregateInfoService,
    private readonly fieldCatalog: FieldCatalogService,
    private readonly dataSource: WorkbenchDataSource,
    private readonly actions: TransactionActionsService,
    private readonly messaging: SessionMessagingService,
    private readonly highlighting: HighlightingService,
    private readonly filterModal: FilterModalService,
    private readonly columnTypeOverrides: ColumnTypeOverrideService,
    private readonly preferences: UserPreferencesService,
    private readonly modal: NgbModal,
    private readonly transactionMetadata: TransactionMetadataSource,
    private readonly listValues: HttpDialogListValuesService,
    private readonly transactionValues: TransactionValueSource,
    private readonly tradeRead: HttpTradeReadService,
    private readonly cdr: ChangeDetectorRef,
    private readonly injector: Injector,
    private readonly toast: ToastService,
    private readonly matches: HttpTransactionMatchesService,
  ) {}

  /** Clearwater exposes the post-date / cleared quick filters and the FX currency picker. */
  get isClearwater(): boolean {
    return this.sourceKey === 'cw'
  }

  ngOnInit(): void {
    this.sourceKey = this.store.sourceValue(this.role)
    this.roleLabel = this.role === 'base' ? 'Base' : 'Comparison'
    this.fxCurrencyFilterItems = this.deriveFxCodes()
    // Seed the grid with the computed columns synchronously so it renders immediately; the
    // catalog columns are fetched asynchronously and merged in once resolved.
    this.applySourceMeta()
    this.colDefs = this.assembleColDefs()
    this.gridOptions = this.createGridOptions()
    this.initToolbarControls()

    // Every page-replacing row request, newest wins — see {@link rowsRequest$}. `switchMap` drops a
    // superseded reload, which matters because each one is a full bounded page (`PAGE_SIZE` rows): the
    // panel is a single bounded client-side page, not a server-side row model.
    //
    // Subscribed BEFORE the catalog pipeline below, and that order is load-bearing: the store streams
    // replay synchronously and a cached or account-less catalog resolves synchronously too, so the
    // first page's `fetchRows()` can fire during that subscribe call. A Subject has no replay, so a
    // later subscription here would silently lose the first page.
    this.rowsRequest$
      .pipe(
        switchMap(mode => {
          if (mode === 'replace') this.pendingRowsReplace = true
          return this.loadRows()
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(rows => {
        const replace = this.pendingRowsReplace
        this.pendingRowsReplace = false
        if (replace) this.applyRows(rows)
        else this.applyRowsPreservingState(rows)
      })

    // Rebuild columns whenever this grid's source or the focused account changes, or the user hits
    // Retry after a retryable (5xx) failure. Both store streams are already distinct-until-changed,
    // so this fires once per real change; switchMap drops a stale in-flight fetch when a newer one
    // supersedes it (avoids racing setRowData).
    //
    // This grid is only ever created once `<hs-cache-load-listener>` (in the panel host) has seen
    // the session cache behind this role reach Done — see transactions-2-panel.component.html. So
    // there is nothing to gate here: by the time this component exists, the fetch is safe to issue.
    merge(this.store.sourceFor(this.role), this.store.accountId$, this.retry$)
      .pipe(
        switchMap(() => {
          this.sourceKey = this.store.sourceValue(this.role)
          // §7: clear the refetch snapshot on a source or account switch. The stored snapshot
          // describes a payload built against the OLD source's catalog; leaving it in place could
          // make the new source's first payload compare equal and suppress a fetch the user needs.
          this.requestSnapshotValue = null
          this.queryFailure = null
          this.applySourceMeta()
          return this.fetchCatalog(this.sourceKey)
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(catalog => this.onCatalogResolved(catalog))

    // "Clear Filters on Load" governs whether switching source or account resets the toolbar,
    // matching the classic transaction grids. With it off, filters persist across the switch.
    merge(
      this.store.sourceFor(this.role).pipe(skip(1)),
      this.store.accountId$.pipe(skip(1)),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.clearFiltersOnAccountLoadToggle.value !== true) return
        this.resetToolbar()
        this.gridApi?.setFilterModel(null)
        this.onExternalFilterChanged()
      })

    this.store
      .crossFilterFor(this.role)
      .pipe(takeUntil(this.destroy$))
      .subscribe(ids => {
        this.crossFilterIds = ids
        this.gridApi?.onFilterChanged()
        this.refreshFilterState()
      })

    this.store
      .relatedRowIdsForOther(this.role)
      .pipe(takeUntil(this.destroy$))
      .subscribe(rowIds => {
        this.relatedRowIds = rowIds
        this.gridApi?.redrawRows()
      })

    this.matching.changes$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.gridApi?.refreshCells({ force: true })
      this.gridApi?.redrawRows()
      this.refreshFilterState()
    })

    // Rows-only refresh. Deliberately NOT merged into the catalog pipeline above: a match changes
    // row data, never the column set, and re-pulling the field catalog per action would be a wasted
    // request.
    this.rowsRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.rowsRequest$.next('preserve'))

    // Server-side filter / sort refetch (§5, §7). Debounced so a burst of changes costs one request.
    //
    // `'preserve'` (not a replace) because a filter or sort change must not throw away the user's
    // column layout or selection — the catalog has not changed, only which rows and in what order.
    // `applyRowsPreservingState` re-applies the same filter model it captured, which fires
    // `onFilterChanged` again; that is harmless because the snapshot now matches, which is the whole
    // point of the guard.
    this.refetch$
      .pipe(
        debounceTime(WorkbenchGridComponent.REFETCH_DEBOUNCE_MS),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.rowsRequest$.next('preserve'))

    // A column's comparison type changed (§4). Three things have to happen, in this order:
    //  1. rebuild the ColDefs, so the new type reaches `filterParams.columnTypeOverride`, the header
    //     glyph and the comparator;
    //  2. destroy the affected columns' live filter instances. `setColumnDefs` alone does NOT refresh
    //     an already-instantiated header filter — this ag-Grid version's `IFilterComp` has no
    //     `refresh(newParams)` hook — so a popover opened BEFORE the change keeps showing the old type,
    //     and a `greaterThan 1000` condition left on a column that is now Text has to be evicted;
    //  3. refetch once, because the filter subtype and sort type on the wire have both changed.
    //
    // `fields: null` is the wildcard a wholesale restore emits: every Aggregate Info column is
    // destroyed. Otherwise ONLY the named fields are touched — an unrelated column's applied predicate
    // must survive a sibling's type change — and `skipDestroy` excludes the one filter instance that is
    // mid-`apply()` right now, because destroying it would blank the condition just typed (ag-Grid
    // disposes a filter via `setModel(null)`) before `filterChangedCallback()` could read it back.
    this.columnTypeOverrides.changes$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ scope, fields, skipDestroy }) => {
        if (scope !== this.overrideScope()) return
        this.colDefs = this.assembleColDefs()
        this.setColumnDefsPreservingState(this.colDefs)
        const targets =
          fields ?? this.aggregateInfoFields().map(column => column.field)
        targets
          .filter(field => !skipDestroy.includes(field))
          .forEach(field => this.gridApi?.destroyFilter(field))
        // Unconditional rather than snapshot-guarded: destroying a filter may leave the filter model
        // byte-identical (the column had no condition), but the TYPE that would be sent for it has
        // changed, and a re-sort has to come from the server. The snapshot is re-stamped by loadRows.
        this.requestSnapshotValue = null
        this.refetch$.next()
        this.cdr.markForCheck()
      })

    // The backend's row-update events (PVAL-26291 CHANGE 4). The POST response is only a command id
    // — the actual match is applied asynchronously through perseus and re-hydrated by
    // flex-transactions — so the grid refreshes from these, not from the response.
    //
    // There are two, one per feed. Both fire for the same change, so this reacts only to the one
    // belonging to the feed it is currently showing — otherwise a single match change would drive
    // two reloads per grid. The check is on the current source rather than on the subscription, so
    // switching a grid's source needs no re-subscribe.
    //
    // The payload is deliberately NOT trusted as this grid's row set: the server passes the whole of
    // `FlexMatchEvent.entityIds` unfiltered into BOTH events, so `rowIds` is a cross-feed union of
    // Enfusion and Prometheus ids. It is used only to answer "does this touch anything I render?" —
    // an id we do not hold is a no-op, not a row we are missing.
    this.messaging
      .on<RowIdPayload, FlexRowUpdateEvent>(EventType.MESSAGE, [
        // Copied: the exported constant is shared module state and this parameter is mutable.
        ...FLEX_ROW_UPDATE_EVENTS,
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event.eventType !== FLEX_UPDATE_EVENT_FOR_SOURCE[this.sourceKey]) {
          return
        }
        if (!this.touchesLoadedRows(event.payload?.rowIds)) return
        // Server truth has arrived, so whatever the optimistic interim guessed is now moot.
        this.actionPending = false
        this.actionPendingNeedsRun = false
        this.rowsRefresh$.next()
      })
  }

  ngOnDestroy(): void {
    this.filterModal.destroy()
    this.destroy$.next()
    this.destroy$.complete()
  }

  // ---- toolbar wiring -------------------------------------------------------

  private initToolbarControls(): void {
    merge(
      this.includeUnmatchedFormControl.valueChanges,
      this.includeIgnoredFormControl.valueChanges,
      this.includeMatchedFormControl.valueChanges,
      this.hideFuturePostDatesToggle.valueChanges,
      this.filterPostDateTodayToggle.valueChanges,
      this.filterClearedToggle.valueChanges,
      this.filterUnclearedToggle.valueChanges,
      this.fxCurrencyFilter.valueChanges,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onExternalFilterChanged())

    this.quickSearch.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.onSearchChanged())
  }

  // ---- source / columns -----------------------------------------------------

  /** Refresh the source-derived metadata (definition, picker options, Create menu, range-sum flag). */
  private applySourceMeta(): void {
    this.source = SOURCE_REGISTRY[this.sourceKey]
    this.sourceOptions = this.optionsForRole()
    this.createMenuItems = this.createMenuItemsForSource()
    // Only Enfusion offers the cell-range Sum readout.
    this.gridContext.rangeSumEnabled = this.sourceKey === 'api'
  }

  /**
   * What the Create menu lists for the current source.
   *
   * Enfusion's is derived from the metadata's `tranCategories` — every schema the document describes
   * is offered, grouped by its own `menuGroup` and labelled with its own `name`. Nothing about that
   * menu is authored here: a schema added, renamed or regrouped in the metadata changes the menu with
   * no UI edit. Every other menu-bearing source lists its own posting types, which stay the stubs
   * they were. Empty when the source has no menu at all, so the list can never be read against a
   * source it does not describe.
   */
  private createMenuItemsForSource(): readonly CreateMenuItem[] {
    if (!this.source.hasCreateMenu) return []
    return this.sourceKey === 'api'
      ? this.transactionMetadata.createMenu()
      : CREATE_TYPES.map(label => ({ label }))
  }

  /**
   * Fetch the field catalog for the current source under the focused account and adapt it to the
   * internal catalog shape. One request per grid (per source); each grid fails independently. An
   * empty catalog (e.g. CW: 0 columns) is legitimate and resolves to an empty column list, not an
   * error.
   *
   * On failure the two modes are distinguished (item 10): a 404 (this account's CW client has no
   * Enfusion metadata) is non-retryable and offers no Retry; a 5xx or transport failure is
   * retryable. Either way the `errorId` from the Helios ReportableError envelope is surfaced so a
   * bug report can be keyed on it, and the sibling grid still renders from its own request.
   */
  private fetchCatalog(sourceKey: SourceKey) {
    const accountId = this.store.accountIdValue()
    this.columnsErrorKind = 'none'
    this.columnsErrorId = null
    this.columnsEmpty = false
    // The API 400s without an accountId; skip the fetch until an account is focused and render the
    // computed columns only.
    if (accountId == null) {
      this.columnsLoading = false
      return of<AdaptedCatalog>(EMPTY_CATALOG)
    }
    this.columnsLoading = true
    this.cdr.markForCheck()
    return this.fieldCatalog.getCatalog(accountId, RPS_SOURCE[sourceKey]).pipe(
      map(catalog => adaptCatalog(catalog)),
      catchError((err: unknown) => {
        // A failure on this grid must not starve the sibling grid: fail this grid's columns
        // independently and fall back to computed columns only, classifying the failure so the
        // template can offer a Retry on the retryable ones.
        const status = err instanceof HttpErrorResponse ? err.status : 0
        // A 404 is a definitive "no metadata for this client" — retrying will keep 404ing. Anything
        // else (5xx, or a status-0 transport failure) is treated as transient and retryable. A
        // FieldCatalogContractError thrown by `adaptCatalog` above — a response with no `isDynamic`
        // to route on — lands here too, and retryable is the right home for it: the alternative is a
        // grid that looks fine and silently filters nothing (PVAL-26129 §3).
        this.columnsErrorKind = status === 404 ? 'not-retryable' : 'retryable'
        this.columnsErrorId = extractErrorId(err)
        this.columnsLoading = false
        return of<AdaptedCatalog>(EMPTY_CATALOG)
      }),
    )
  }

  /** Assemble the full ColDef list from the current catalog columns + runtime Aggregate Info. */
  private assembleColDefs(): ColDef[] {
    // A successful fetch that returned nothing is a real answer, not a gap to paper over: the
    // source genuinely has no catalogue (Clearwater today). Falling back to computed columns here
    // would contradict the empty-state message sitting directly above the grid. The fallback is
    // for "we could not ask" (no focused account) and "we asked and it failed" — the latter says
    // so in its own strip.
    if (this.columnsEmpty) return []

    return buildColDefs(
      this.source,
      this.matching,
      this.catalogColumns,
      this.discoveredAggColumns(),
      this.gridContext,
      {
        sourceKey: this.sourceKey,
        typeOverrideScope: this.overrideScope(),
        overrideFor: field =>
          this.columnTypeOverrides.typeFor(this.overrideScope(), field),
        columnTypeOverrideService: this.columnTypeOverrides,
      },
    )
  }

  /**
   * The {@link ColumnTypeOverrideService} scope this grid's Aggregate Info type choices live under.
   *
   * Per panel and per SOURCE, but deliberately NOT per role (§4): the same `field` string means an
   * untyped Prometheus `agg_info` key on one source and possibly nothing at all on another, so the
   * source has to be in the key — while two grids showing the SAME source must agree, so the role must
   * not be. (Contrast `filterGroupsToolPanelParams`, which is per role because saved filter groups are
   * per grid.)
   */
  private overrideScope(): string {
    return `${Transactions2Key}:${this.sourceKey}`
  }

  /**
   * Every catalog column this grid can filter or sort on: the fetched catalog plus the Aggregate Info
   * columns discovered from the loaded rows.
   *
   * Discovered columns have to be in here or a filter on one would be dropped by the request builder's
   * allow-list — they are real `agg_info` keys, just ones the catalog had not observed yet.
   */
  private queryableColumns(): ColumnCatalogEntry[] {
    return [...this.catalogColumns, ...this.discoveredAggColumns()]
  }

  /** The Aggregate Info columns, catalogued and discovered — the ones that carry a type override. */
  private aggregateInfoFields(): ColumnCatalogEntry[] {
    return this.queryableColumns().filter(
      column => column.colSource === AGGREGATE_INFO_GROUP,
    )
  }

  /**
   * ag-Grid's sort model in the user's chosen precedence order.
   *
   * Read off the COLUMN api rather than `gridApi.getSortModel()`: in this ag-Grid version sort state
   * lives on the column state, and `sortIndex` is the only place precedence is recorded. Sorting by it
   * matters — `sort` is an ordered array, so getting the order wrong silently sorts by the wrong column
   * first.
   */
  private sortModel(): GridSortModelEntry[] {
    return (this.columnApi?.getColumnState() ?? [])
      .filter(column => column.sort != null && column.colId != null)
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      .map(column => ({
        colId: String(column.colId),
        sort: String(column.sort),
      }))
  }

  /**
   * Everything the request builder needs, resolved fresh on every call.
   *
   * Never cached: a type override can change between two fetches, and a stale `meta` would send the
   * previous type — which is precisely the 400/500 this panel cannot see coming.
   *
   * Built even before the grid exists (no column filters, no sort yet), because the status
   * checkboxes are part of the payload from the very first page: the first fetch has to carry them,
   * or the grid-ready `onFilterChanged` would find a different snapshot and fetch the page twice.
   */
  private requestInput(): RequestFilterInput {
    return {
      filterModel: this.gridApi?.getFilterModel() ?? {},
      sortModel: this.sortModel(),
      meta: buildFieldMeta(
        this.queryableColumns(),
        field => this.columnTypeOverrides.typeFor(this.overrideScope(), field),
        STATIC_UNSORTABLE_FIELDS[this.sourceKey],
      ),
      matchStatus: this.statusCheckboxConstraint(),
      search: this.searchConstraint(),
    }
  }

  /**
   * The status checkboxes' server-side constraint (see `grid/filters/match-status-filter.ts`), or null
   * when they must not narrow the request.
   *
   * Null on a source with no checkboxes, and null while a match-count cross-filter is active: the
   * cross-filter wins outright client-side ("show me exactly these rows"), and those rows are usually
   * MATCHED — so a page narrowed to the default Unmatched would not contain them and the cross-filter
   * would show nothing. Setting or clearing the cross-filter therefore costs one refetch.
   */
  private statusCheckboxConstraint(): string[] | null {
    if (!this.source?.hasStatusFilter || this.crossFilterIds != null) {
      return null
    }
    return serverMatchStatusValues(this.statusFilterState())
  }

  /**
   * Replace the grid's ColDefs without losing what the user has done in the Column Manager.
   *
   * `setColumnDefs` replaces the whole column model — visibility, order and width included — so a raw
   * call on a type-override change would reset every column to the catalog's defaults just because
   * the user picked Number for one Aggregate Info column. `applyColumnState` is keyed by colId, so the
   * captured layout (sort included) goes straight back on. Mirrors positions-2's method of the same
   * name.
   */
  private setColumnDefsPreservingState(colDefs: ColDef[]): void {
    const state = this.columnApi?.getColumnState()
    this.gridApi?.setColumnDefs(colDefs)
    if (state) this.columnApi?.applyColumnState({ state, applyOrder: true })
  }

  /**
   * Refetch if — and only if — the server-side predicate actually changed.
   *
   * Called from BOTH `onFilterChanged` and `onSortChanged`, because both change the payload and both
   * also fire for reasons that do not. Everything client-only routes through `onFilterChanged` too
   * (quick search, the Clearwater quick filters, and `applyRows`'s own call), and none of those touch
   * the payload — so their snapshot is identical and they cost zero requests, which is what §7's
   * "infinite-loop trap" is about. The status checkboxes and the cross-filter DO move the payload
   * (through `matchStatus`), so a change to either costs exactly one request.
   */
  private maybeRefetch(): void {
    if (!this.gridApi) return
    const snapshot = requestSnapshot(this.requestInput())
    if (snapshot === this.requestSnapshotValue) return
    this.refetch$.next()
  }

  /** ag-Grid told us the filter or sort changed: keep the toolbar in step, then decide on a refetch. */
  private onFilterOrSortChanged(): void {
    this.refreshFilterState()
    this.maybeRefetch()
  }

  /**
   * Column-manager grouping (colId -> group) for the current source. Computed columns are
   * attributed to the Helios group; catalog and Aggregate Info columns carry their own.
   */
  colGroups(): Record<string, string> {
    return colSourceMap(
      buildComputedColumns(this.source, this.matching),
      this.catalogColumns,
      this.discoveredAggColumns(),
    )
  }

  /**
   * Aggregate Info columns found in the loaded rows but absent from the catalog.
   *
   * Shared by the ColDef build and the grouping map so the two can never see a different set —
   * when they did, the columns only one of them knew about lost their section.
   */
  private discoveredAggColumns(): ColumnCatalogEntry[] {
    if (!this.source.hasAggregateInfo) return []
    return this.aggInfo.discoverColumns(
      this.store.rowsForSource(this.sourceKey),
      new Set(this.catalogColumns.map(column => column.field)),
    )
  }

  /** Column Manager section order for the current source. */
  colGroupOrder(): string[] {
    return this.catalogGroupOrder
  }

  /**
   * Retry a retryable (5xx / transport) catalog failure. Re-drives the same fetch pipeline for the
   * current source. A no-op unless the current failure is retryable, so the template only wires it
   * to the retryable state's button.
   */
  retryFetch(): void {
    if (this.columnsErrorKind !== 'retryable') return
    this.retry$.next()
  }

  /** Apply a resolved (possibly empty) catalog to the grid. */
  private onCatalogResolved(catalog: AdaptedCatalog): void {
    const columns = catalog.columns
    this.catalogColumns = columns
    this.catalogGroupOrder = catalog.groupOrder
    this.columnsLoading = false
    this.updateSearchAvailability()
    // An empty catalog is only meaningful once an account is focused (before that we simply
    // haven't fetched); a focused account returning zero columns is legitimate (e.g. CW).
    this.columnsEmpty =
      this.store.accountIdValue() != null &&
      !this.columnsError &&
      columns.length === 0
    this.colDefs = this.assembleColDefs()
    if (this.gridApi) {
      this.gridApi.setColumnDefs(this.colDefs)
      this.gridApi.setRowData(this.store.rowsForSource(this.sourceKey))
      this.gridApi.onFilterChanged()
      this.refreshFilterState()
    }
    this.cdr.markForCheck()
    this.fetchRows()
  }

  /**
   * Fetch this source's rows, which can only happen once its catalog has resolved: the query names
   * the fields it wants and those names come from the catalog. Rows go into the store rather than
   * straight onto the grid so the matching index spans both sources.
   */
  private fetchRows(): void {
    this.rowsRequest$.next('replace')
  }

  /**
   * One row fetch for the current source, as an observable so the initial load, the rows-only refresh
   * and the filter/sort refetch can all drive it. Emits at most once, and emits nothing at all when a
   * source switch landed while the request was in flight — the stale page must not overwrite the new
   * one.
   *
   * This is the ONLY place a payload is built, which is also why it is the only place the refetch
   * snapshot is stamped: the snapshot has to describe what the page on screen was actually fetched
   * with, or the loop guard is comparing against a fiction.
   */
  private loadRows(): Observable<WorkbenchRow[]> {
    const fields = this.catalogColumns.map(column => column.field)
    if (!fields.length) return of<WorkbenchRow[]>([])

    const input = this.requestInput()
    const requestFilter: RequestFilter | null = buildRequestFilter(input)
    const sort: QuerySort[] | null = buildSort(input)
    // Through the builder's own serializer, so the stamp and `maybeRefetch`'s comparison can never
    // drift apart: this string is what keeps a quick-search keystroke at zero requests.
    this.requestSnapshotValue = serializeRequest(requestFilter, sort)

    this.rowsLoading = true
    this.queryFailure = null
    // ag-Grid's own overlay, over the grid BODY with the headers left visible — the house pattern
    // (`distributions`, `external-file-manager`, `positions/external-comparison` all do this). That
    // reads correctly for this stage: the columns are already built from the fields API, and it is
    // the data that is still coming. Masking the whole area instead (as the catalog fetch does) hid
    // the headers and looked like the grid had not loaded at all.
    //
    // No explicit hide is needed on success: `setRowData` clears it, and swaps in the no-rows
    // overlay when the page really is empty.
    this.gridApi?.showLoadingOverlay()
    this.cdr.markForCheck()

    const sourceKey = this.sourceKey
    return this.dataSource.load(sourceKey, fields, requestFilter, sort).pipe(
      catchError((err: unknown) => {
        // As with the catalog, one grid's failure must not starve the other.
        //
        // The classification only ADDS to the presentation for INVALID_FIELD_TYPE, which is actionable
        // per column (Reset to Text). flex-transactions does not send that 400 yet, so today every
        // failure — including the 500 an un-castable dynamic value produces — is the toast alone.
        this.queryFailure = classifyQueryError(err)
        // A request superseded by a source switch has nothing left to say about the grid on screen.
        if (sourceKey === this.sourceKey) this.presentRowsFailure(err)
        // EMPTY rather than `of([])`, so no caller judges anything against a page that never came:
        // the post-action reconcile would otherwise read "not applied" off an empty page. The failure
        // has already been presented — toast, empty table, loading cleared — so nothing downstream
        // has to.
        return EMPTY
      }),
      // A source switch may have landed while this was in flight; discard the stale page rather than
      // overwriting the newer source's rows with it. The mask is cleared on the way out — the fetch
      // this page was superseded by sets its own, and without this a discarded page leaves the grid
      // masked forever.
      filter(() => {
        if (sourceKey === this.sourceKey) return true
        this.rowsLoading = false
        this.gridApi?.hideOverlay()
        this.cdr.markForCheck()
        return false
      }),
    )
  }

  /**
   * Show a failed row fetch for what it is: an error toast, the loading spinner down, and an EMPTY
   * table rather than the previous page.
   *
   * Empty, not the previous rows: those answered a different filter / sort / type, and leaving them
   * on screen under the new predicate shows rows the user did not ask for as though they had been. The
   * store is cleared with the grid so the sibling's match counts and the status bar agree with what is
   * shown. The toast is the whole report — there is no in-grid error banner, except the Reset to Text
   * strip for an INVALID_FIELD_TYPE 400. Changing the filter, sort or type re-queries.
   *
   * The toast carries the backend's own message — for an un-castable Aggregate Info type that is the
   * Postgres one, e.g. `invalid input syntax for type numeric: "USD"`, which names the offending value
   * — and its error id, so the report can be traced.
   */
  private presentRowsFailure(err: unknown): void {
    const label = this.source?.label ?? 'Transaction'
    this.toast.error(
      `${label} rows could not be loaded: ${this.failureDetail(err)}`,
      extractErrorId(err),
    )

    this.rowsLoading = false
    this.store.setRowsForSource(this.sourceKey, [])
    this.fxCurrencyFilterItems = this.deriveFxCodes()
    // `setRowData` takes the loading overlay down and swaps in the no-rows one.
    this.gridApi?.setRowData([])
    this.refreshFilterState()
    this.cdr.markForCheck()
  }

  /** The most specific sentence available for a failed query. */
  private failureDetail(err: unknown): string {
    if (this.queryFailure?.kind === 'invalid-field-type') {
      return invalidFieldTypeMessage(this.queryFailure)
    }
    if (err instanceof HttpErrorResponse) {
      const envelope = err.error as ReportableErrorEnvelope | null | undefined
      return (
        envelope?.errorMessage ??
        envelope?.displayMessage ??
        `the server answered ${err.status || 'with no response'}.`
      )
    }
    return 'an unexpected error occurred.'
  }

  /** The sentence shown for an INVALID_FIELD_TYPE failure. Empty for every other kind. */
  get queryFailureMessage(): string {
    return this.queryFailure?.kind === 'invalid-field-type'
      ? invalidFieldTypeMessage(this.queryFailure)
      : ''
  }

  /** Whether the Reset-to-Text affordance applies to the current failure. */
  get canResetTypesToText(): boolean {
    return this.queryFailure?.kind === 'invalid-field-type'
  }

  /**
   * Clear the comparison-type overrides the backend rejected and re-query (§6).
   *
   * Clears EVERY field the 400 named, not one: Postgres reports the offending value rather than the
   * column it came from, so the error names every field that claimed the failing type and resetting
   * them one at a time would leave the retry able to fail identically.
   *
   * `clear()` returns whether it actually emitted. It legitimately may not — the 400's `fields` can be
   * empty, or can name a column whose type was never overridden — and in that case nothing will arrive
   * on `changes$` to drive the refetch, so this has to drive it itself.
   */
  resetTypesToText(): void {
    const failure = this.queryFailure
    if (failure?.kind !== 'invalid-field-type') return

    const cleared = this.columnTypeOverrides.clear(
      this.overrideScope(),
      failure.fields,
    )
    this.queryFailure = null
    if (!cleared) {
      this.requestSnapshotValue = null
      this.refetch$.next()
    }
    this.cdr.markForCheck()
  }

  private applyRows(rows: WorkbenchRow[]): void {
    this.rowsLoading = false
    this.store.setRowsForSource(this.sourceKey, rows)
    this.fxCurrencyFilterItems = this.deriveFxCodes()
    if (this.gridApi) {
      // Aggregate Info columns are discovered from the rows, so the ColDefs are rebuilt now that
      // the page has landed.
      this.colDefs = this.assembleColDefs()
      this.gridApi.setColumnDefs(this.colDefs)
      this.gridApi.setRowData(rows)
      this.gridApi.onFilterChanged()
      this.refreshFilterState()
    }
    this.cdr.markForCheck()
  }

  /**
   * Apply a rows-only refresh, preserving everything the user set up around the rows.
   *
   * The panel holds a single bounded client-side page (`PAGE_SIZE` rows) per source
   * (`http-workbench-data-source.ts`), so "refresh the rows" is unavoidably a whole-source reload —
   * there is no server-side row model to invalidate a slice of. What must survive it:
   *
   *  - **column state** (order, widths, visibility): preserved by NOT calling `setColumnDefs`. The
   *    catalog has not changed, so re-setting the defs would reset the user's layout for nothing.
   *    The cost is that a newly-appearing Aggregate Info key is not discovered until the next real
   *    catalog resolve, which is a much smaller loss than resetting the layout on every match.
   *  - **filter model**: captured and re-applied, because replacing the row data re-derives set
   *    filter values.
   *  - **selection**: re-applied by row id. `getRowNodeId` is the row id, but the row objects are new
   *    instances, so ag-Grid cannot carry selection across `setRowData` on its own.
   *  - **cross-filter**: component state (`crossFilterIds`), untouched by a reload.
   *
   * @param rows the freshly fetched page
   */
  private applyRowsPreservingState(rows: WorkbenchRow[]): void {
    if (!this.gridApi) {
      this.applyRows(rows)
      return
    }

    const filterModel = this.gridApi.getFilterModel()
    const selectedIds = new Set(this.selectedRows().map(row => row.rowId))

    this.rowsLoading = false
    this.store.setRowsForSource(this.sourceKey, rows)
    this.fxCurrencyFilterItems = this.deriveFxCodes()

    this.gridApi.setRowData(rows)
    this.gridApi.setFilterModel(filterModel)
    if (selectedIds.size) {
      this.gridApi.forEachNode(node => {
        const row = node.data as WorkbenchRow | undefined
        if (row && selectedIds.has(row.rowId)) node.setSelected(true)
      })
    }
    this.gridApi.onFilterChanged()
    this.refreshFilterState()
    this.cdr.markForCheck()
  }

  private optionsForRole(): SourceOption[] {
    return (Object.keys(SOURCE_REGISTRY) as SourceKey[])
      .filter(key => SOURCE_REGISTRY[key].allowedRoles.includes(this.role))
      .map(key => {
        const definition = SOURCE_REGISTRY[key]
        return {
          key,
          label: definition.label,
          tag: definition.tag,
          tagColor: definition.tagColor,
          disabled: !isSourceSelectable(this.role, key),
        }
      })
  }

  onSourcePicked(key: SourceKey): void {
    // The menu item is disabled, so this is belt-and-braces; the store refuses it regardless.
    if (!isSourceSelectable(this.role, key)) return
    this.store.setSource(this.role, key)
  }

  /**
   * Open/close the Create menu's "Trade" flyout on hover, as the prototype does: entering "Trade"
   * opens it, entering a sibling (GL Entry) closes it again.
   */
  setCreateTradeSubmenu(open: boolean): void {
    if (this.createTradeSubmenuOpen === open) return
    this.createTradeSubmenuOpen = open
  }

  /** Collapse the Trade flyout whenever the Create menu itself opens or closes, so it never opens
   * pre-expanded from a previous visit. */
  onCreateMenuOpenChange(): void {
    this.createTradeSubmenuOpen = false
  }

  /**
   * Entry point 1: Create opens the metadata-driven Create Transaction modal — but only for Enfusion,
   * and only for a tran category the menu named.
   *
   * The menu asks which document you are creating first — one of the fourteen trade types, or a GL
   * entry — and the picked `tranCategory` is what decides which schema the modal builds. There is
   * deliberately **no default**: an omitted tran category is refused rather than quietly becoming a Listed
   * trade, which would build the wrong schema the same way an unreachable default in
   * `resolveTranCategory` would.
   *
   * Clearwater also exposes Create (through its menu of posting types), and those flows live in the
   * existing `create-transactions-modal` lib, which is explicitly out of scope for PVAL-26341. So
   * anything that is not the `api` source stays the stub it was, rather than opening an Enfusion
   * dialog over a Clearwater grid; its items carry no tran category either, so both halves of the guard
   * agree.
   */
  onCreate(tranCategory?: TranCategoryKey): void {
    if (this.sourceKey !== 'api' || !tranCategory) return undefined
    // Create mode is handed its tran category directly; `resolveTranCategory` is never called without a row.
    this.openTransactionModal(
      'create',
      { kind: 'tranCategory', tranCategory },
      null,
    )
  }

  /**
   * Entry points 2 + 3: the Enfusion context menu's Edit Transaction, and row double-click.
   *
   * Opens at once. No read gates the dialog: the trade, the catalog lists and the server matches
   * (PVAL-26451, see `readMatches`) all land on the open dialog under its own loader.
   */
  editTransaction(row: WorkbenchRow): void {
    this.openTransactionModal(
      'edit',
      resolveTranCategory(row, this.transactionMetadata),
      row,
    )
  }

  /** Entry point 5 (PVAL-26495): Edit Transaction over a multi-row Enfusion selection. */
  editTransactionsBulk(rows: WorkbenchRow[]): void {
    if (rows.length === 1) {
      this.editTransaction(rows[0])
      return
    }
    this.openBulkTransactionModal(
      'edit',
      rows.map(row => ({
        row,
        resolution: resolveTranCategory(row, this.transactionMetadata),
      })),
    )
  }

  /**
   * Entry point 4: Post Enfusion on the Prometheus menu. The other Post labels remain placeholders
   * (they target Clearwater posting flows in other panels), so they are ignored here rather than
   * opening an Enfusion dialog.
   *
   * The tran category is the one the picker named and is never inferred from the row — a Prometheus row
   * carries neither `gLEntryId` nor a `tradeType`, so there is nothing to infer it from. An omitted
   * tran category is refused for the same reason Create refuses one: quietly defaulting to a Listed trade
   * would build the wrong schema for a GL entry.
   *
   * Several rows post as a bulk grid on the same chosen tran category (PVAL-26495). Nothing is
   * inferred per row, so there is no per-row resolution to disagree about: the category the picker
   * named is every row's, which is what makes the grid one schema's columns by construction rather
   * than by a check.
   */
  postTransaction(
    label: string,
    scopeRows: WorkbenchRow[],
    tranCategory?: TranCategoryKey,
  ): void {
    if (label !== POST_ENFUSION_LABEL || !tranCategory) return
    if (!scopeRows.length) return
    const resolution: TranCategoryResolution = {
      kind: 'tranCategory',
      tranCategory,
    }
    if (scopeRows.length === 1) {
      this.openTransactionModal('post', resolution, scopeRows[0])
      return
    }
    this.openBulkTransactionModal(
      'post',
      scopeRows.map(row => ({ row, resolution })),
    )
  }

  /**
   * Assemble the modal's view model here — the host owns resolution, the row's matched counterparts
   * and the two metadata/value seams — and hand the finished projection to a dumb component.
   *
   * The modal saves for itself (PVAL-26440) and closes with the created commands, which only exist
   * as pending commands in the drawer — so the host acts on nothing it returns, and the workbench row
   * is never mutated (refreshing the grid after a save is out of scope).
   *
   * An edit also reads the row's server matches (PVAL-26451) once the dialog is open. When they
   * land they are merged with the in-session counterparts ONCE — fetched first, because they are
   * server truth and the projection's per-origin `find` takes the first hit — and an in-session
   * entry already present by `(origin, rowId)` is dropped. Every later re-projection (trade read,
   * catalog resolve) reuses that list, so nothing on the dialog can re-trigger the read.
   */
  private openTransactionModal(
    mode: EditTransactionMode,
    resolution: TranCategoryResolution,
    row: WorkbenchRow | null,
  ): void {
    let counterparts = row ? this.prometheusCounterparts(row) : []
    const ref = this.modal.open(EditTransactionModalComponent, {
      // `NgbModal.open` builds the component from the MODULE injector, so the panel's three seams —
      // declared on `Transactions2PanelComponent.providers`, i.e. an ELEMENT injector — are invisible
      // to it. The modal injects `TransactionDerivationSource` in its constructor, so without this
      // the dialog dies on open with `R3InjectorError(AppModule)[TransactionDerivationSource]`.
      //
      // This grid sits inside the panel, so its own injector sees all three. Handing it over is what
      // makes a seam declared on the panel reachable from the dialog the panel opens. The other two
      // seams never needed it only because the grid resolves them itself and passes finished data.
      injector: this.injector,
      // No Bootstrap size class: the dialog is pinned to the width of its own columns (see
      // `$dialog-width`), which `xl` overshot by ~200px. `windowClass` is how the stylesheet reaches
      // the modal window, since it is rendered outside this component.
      // `hs-modal-window` FIRST, and never alone: the app sets it as the global default
      // windowClass (app.module.ts) and it carries `pointer-events: none`, which is what lets
      // the workbench behind a dialog still take a click — every legacy dialog relies on it.
      // Passing a windowClass REPLACES that default rather than adding to it, so a dialog
      // naming its own was silently swallowing every click on the page behind it.
      windowClass: 'hs-modal-window et-modal-window',
      centered: true,
      scrollable: true,
      // PVAL-26440: ESC and the backdrop must not close a dialog whose save is in flight — a reopen
      // and a second Save would create the commands twice. Read when a dismissal is attempted, by
      // which time `ref` exists.
      beforeDismiss: () =>
        !(ref.componentInstance as EditTransactionModalComponent).saving,
    })
    const instance = ref.componentInstance as EditTransactionModalComponent
    const project = (source: WorkbenchRow | null) =>
      projectEditTransaction({
        mode,
        resolution,
        row: source,
        counterparts: source ? counterparts : [],
        metadata: this.transactionMetadata,
        values: this.transactionValues,
        // A create draft has no row to read an account off, so it opens under the one in focus.
        accountId: this.focusedAccountId(),
        // Catalog rows missing a display column report into the dialog's own once-only log.
        onBlankCell: field => instance.blankCellReporter(field),
      })

    // Up to THREE fills land asynchronously on this dialog — the trade read, the catalog resolve
    // and, on an edit, the server matches — in any order. They share one owner of `instance.vm`
    // so each re-projects over what the others left, rather than projecting the original row again
    // and silently dropping it.
    let current = row
    const render = (source: WorkbenchRow | null = current): void => {
      current = source
      instance.vm = project(source)
    }

    render(row)
    this.resolveOnOpen(
      instance,
      accountIdOf(row, this.focusedAccountId()),
      () => render(),
    )
    this.prefillFromRecord(instance, resolution, row, render)
    // The matches read belongs to THIS dialog, not to the grid: closing the dialog mid-read must
    // drop it, or a late failure toasts about a dialog that is gone (and reads as if it were about
    // whichever Edit is open now) and a late success re-projects onto a destroyed component.
    const closed$ = new Subject<void>()
    if (mode === 'edit' && row) {
      this.readMatches(instance, row, closed$, fetched => {
        counterparts = mergeCounterparts(
          fetched,
          this.prometheusCounterparts(row),
        )
        render()
      })
    }

    const close = (): void => {
      closed$.next()
      closed$.complete()
    }
    ref.result.then(close, close)
  }

  /**
   * The account the workbench is focused on, as a dialog names an account — or null when none is.
   *
   * What a dialog opened over no record (Create) belongs to, and what stands in for a record that
   * carries no account of its own. Raw, never the chip's text: it is what `/resolve` is called with
   * and what helios checks against the session.
   */
  private focusedAccountId(): string | null {
    const account = this.store.accountIdValue()
    return account == null ? null : String(account)
  }

  /**
   * The dialog's ONE opening `/resolve` call (PVAL-26685): the outstanding catalogs and the
   * dialog's `context` derivations together, for the account the dialog is opened on.
   *
   * The modal stopped deriving on every view-model assignment — that would be one call per
   * re-render, three on an edit open — so the opening derivations ride along here and their results
   * are handed back through `applyDerivationResults`. With the offline flag off the derivations are
   * answered by the bound static source instead, and only the lists travel.
   */
  private resolveOnOpen(
    instance: EditTransactionModalComponent,
    accountId: string,
    reproject: () => void,
  ): void {
    const derivations = instance.openingDerivationRequests()
    if (!USE_HTTP_TRANSACTION_DERIVATION) {
      if (derivations.length) {
        this.injector
          .get(TransactionDerivationSource)
          .resolve(derivations, accountId)
          .subscribe(results => {
            instance.applyDerivationResults(results)
            this.cdr.markForCheck()
          })
      }
      this.resolveListValues(accountId, reproject)
      return
    }
    this.resolveListValues(accountId, reproject, derivations, results => {
      instance.applyDerivationResults(results)
    })
  }

  /**
   * Fill in the `catalog` lists the metadata document serves WITHOUT their options (PVAL-26443).
   *
   * One call for every outstanding key, fired as the dialog opens and alongside the trade read
   * rather than before it: the two answer different questions and neither waits on the other, so
   * a slow catalogue never holds up the values and a slow trade never holds up the pickers.
   *
   * Re-projects on success, because `listPending` and the picker's options are both read at
   * projection time. Nothing is re-projected on failure: the fields stay pending and their
   * controls stay disabled, which is what they already were — and unlike the trade read this does
   * NOT freeze the dialog, because an unresolvable catalogue costs the user one picker rather than
   * risking a save over values that were never loaded.
   *
   * Resolves once per panel in practice: the options are merged onto the metadata source, so the
   * second dialog finds nothing outstanding and makes no call at all.
   */
  private resolveListValues(
    accountId: string,
    reproject: () => void,
    derivations: readonly DerivationRequest[] = [],
    onDerivations?: (results: DerivationResult[]) => void,
  ): void {
    this.listValues
      .resolveOutstanding(accountId, derivations)
      .subscribe(resolved => {
        // Derivation results first, and on every outcome: a whole-call failure arrives as an error
        // on each derivation, which is what marks those fields failed rather than leaving them
        // pending forever.
        if (onDerivations && resolved.derivations.length) {
          onDerivations(resolved.derivations)
          this.cdr.markForCheck()
        }
        if (resolved.error != null) {
          console.error(
            '[transactions-2] could not resolve the dialog\u2019s catalog lists; those pickers stay ' +
              'empty and disabled',
            resolved.error,
          )
          return
        }
        // Per-key failures are NOT fatal and do not stop the merge: the keys that did resolve are
        // still worth showing. Logged once, with the keys, because a silently disabled picker looks
        // exactly like a field nobody configured.
        if (resolved.failures.size) {
          console.error(
            '[transactions-2] some catalog lists did not resolve; their pickers stay empty and ' +
              'disabled',
            Object.fromEntries(resolved.failures),
          )
        }
        if (resolved.options.size === 0) return
        this.transactionMetadata.applyResolvedLists(resolved.options)
        reproject()
        this.cdr.markForCheck()
      })
  }

  /**
   * Replace the dialog's grid-projected values with the whole record, once helios answers: the
   * trade behind a trade row, the journal entry behind a GL Entry row.
   *
   * The dialog opens on the row the grid already has and swaps in the fuller document when it
   * arrives, rather than waiting on the fetch — the same shape as `HttpTransactionMetadataService`,
   * and for the same reason: a dialog that opens instantly with less is better than one that opens
   * late with more. The consequence is real and visible: a dialog dismissed inside the round trip
   * never shows the fetched values.
   *
   * Which read it is, is decided by the id the row carries (`recordSources`), and none runs unless
   * the row carries one — which is the condition itself, not a proxy for one. Create mode has no
   * row. A journal entry has a `gLEntryId` and no trade id, and `trades/get` routes to Enfusion's
   * TRADE endpoints, which it has no row in — so it reads `gl/journalentries/get` instead. Gating on
   * the id rather than on the tran category keeps this free of any one client's category
   * vocabulary: whatever has a GL entry id gets a GL read, whatever else has a trade id gets a trade
   * read.
   */
  private prefillFromRecord(
    instance: EditTransactionModalComponent,
    resolution: TranCategoryResolution,
    row: WorkbenchRow | null,
    render: (source: WorkbenchRow | null) => void,
  ): void {
    if (!row) return
    const source = this.recordSources.find(candidate =>
      idText(row.fields[candidate.idField]),
    )
    if (!source) {
      // Said out loud rather than returning quietly. Without an id there is no fetch, so there is
      // also no loader and no prefill — three absences that look exactly like "the endpoint
      // answered nothing". `tradeId` and `gLEntryId` are requested on every fetch
      // (`REQUIRED_FIELDS.api`), so a row with neither means the read API did not answer them, which
      // is a fixable fact rather than a property of the record.
      console.error(
        '[transactions-2] no tradeId or gLEntryId on this row, so the Edit dialog cannot read the ' +
          'record; it keeps its grid-projected values. REQUIRED_FIELDS.api asks for both — check ' +
          'the read API answers one.',
        { rowId: row.rowId, origin: row.origin },
      )
      return
    }
    const recordId = idText(row.fields[source.idField])

    const tranCategory =
      resolution.kind === 'tranCategory' ? resolution.tranCategory : undefined

    instance.loading = true
    // Cleared before the request, not only on success: a dialog reopened after a failure must not
    // show the previous attempt's error while the new one is still in flight.
    instance.errorLabel = undefined
    instance.errorHeader = undefined
    instance.errorDetails = null

    source
      .read([recordId], accountIdOf(row, this.focusedAccountId()), tranCategory)
      // `finalize`, not just the next handler: the spinner must clear however the stream ends.
      // An observable that COMPLETES WITHOUT EMITTING never reaches `next`, and that is not
      // hypothetical here — the app's global `ErrorInterceptor` does exactly that on every HTTP
      // error unless a request opts out, which is what left this dialog spinning on a 404.
      .pipe(finalize(() => this.clearLoading(instance)))
      .subscribe(result => {
        // Cleared on every outcome, including a failed or empty read: the service RETURNS its error
        // rather than throwing, so a `complete`-only reset would leave the spinner up forever on the
        // one path the user most needs told about.
        instance.loading = false
        // A failed read FREEZES the dialog rather than revealing a half-filled form — see
        // `freezeOnError`. Nothing below runs, so no partial document is projected over the row.
        if (result.error != null) {
          this.freezeOnError(instance, result.error)
          return
        }

        const document = result.documents.get(recordId)
        if (!document) {
          this.cdr.markForCheck()
          return
        }
        // The document's keys ARE the dialog's field names: `transaction_dialog_metadata` is
        // transcribed from Enfusion's own schema for the record, so the read needs no mapping layer.
        // The row's own fields stay underneath as the fallback for anything the record omits.
        render({
          ...row,
          fields: { ...row.fields, ...document },
        })
        this.cdr.markForCheck()
      })
  }

  /**
   * Read an edited row's server-matched Custody/Trading counterparts (`POST transactions/matches`,
   * PVAL-26451) into a dialog that is ALREADY open, under its "Loading Matches…" overlay.
   *
   * The key is taken from the grid row AS RECEIVED — `transactionId` is the row id
   * (`ROW_ID_FIELD.api`) and `transactionType` the read API's own column — and never from the trade
   * document `prefillFromRecord` merges in, whose `transactionType` is Enfusion's write-side
   * vocabulary, not the flex primary key.
   *
   * A row with no usable key (a synthetic id, or no `transactionType`) is logged and keeps its
   * in-session counterparts only. A failed read toasts and leaves the comparison empty; it does not
   * freeze the dialog the way a failed trade read does, because only the comparison is missing.
   *
   * The read lives exactly as long as the dialog it was opened for: `closed$` (completed by the
   * dialog's own `ref.result`) or the grid's teardown ends it, and nothing — toast or re-projection —
   * lands after either.
   */
  private readMatches(
    instance: EditTransactionModalComponent,
    row: WorkbenchRow,
    closed$: Observable<void>,
    apply: (fetched: WorkbenchRow[]) => void,
  ): void {
    const transactionType = String(row.fields['transactionType'] ?? '').trim()
    if (!hasServerRowId(row) || transactionType === '') {
      console.error(
        '[transactions-2] cannot read matched counterparts for this row — it has no server ' +
          'transactionId or no transactionType; the Edit dialog shows none. ' +
          'REQUIRED_FIELDS.api asks for both — check the read response.',
        { rowId: row.rowId, transactionType: row.fields['transactionType'] },
      )
      return
    }

    instance.matchesLoading = true
    this.matches
      .load({ transactionId: row.rowId, transactionType }, row.accountId)
      .pipe(
        takeUntil(merge(this.destroy$, closed$)),
        // `finalize`, for the trade read's reason: the overlay must drop however the stream ends.
        finalize(() => {
          instance.matchesLoading = false
          this.cdr.markForCheck()
        }),
      )
      .subscribe(result => {
        instance.matchesLoading = false
        if (result.error != null) {
          this.toast.error(MATCHES_READ_ERROR)
        } else if (result.custody.length || result.trading.length) {
          apply([...result.custody, ...result.trading])
        }
        this.cdr.markForCheck()
      })
  }

  /** Open the Bulk Transactions grid over a selection. */
  private openBulkTransactionModal(
    mode: BulkTransactionMode,
    entries: BulkTransactionEntry[],
  ): void {
    const ref = this.modal.open(BulkTransactionModalComponent, {
      injector: this.injector,
      // `hs-modal-window` FIRST, and never alone: the app sets it as the global default
      // windowClass (app.module.ts) and it carries `pointer-events: none`, which is what lets
      // the workbench behind a dialog still take a click — every legacy dialog relies on it.
      // Passing a windowClass REPLACES that default rather than adding to it, so a dialog
      // naming its own was silently swallowing every click on the page behind it.
      windowClass: 'hs-modal-window bt-modal-window',
      centered: true,
      // NOT `scrollable`: it adds `.modal-dialog-scrollable`, whose `.modal-content { overflow:
      // hidden }` clips the anchored panels to the dialog — a calendar opened on a low row then had
      // nowhere to go but over the header. The grid scrolls itself and `.bt-body` caps its own
      // height, so the modal has nothing left to scroll anyway.
      // PVAL-26440: no ESC / backdrop dismissal while a save is in flight — see the single dialog.
      beforeDismiss: () =>
        !(ref.componentInstance as BulkTransactionModalComponent).saving,
    })
    const instance = ref.componentInstance as BulkTransactionModalComponent
    // What the opening derivations have answered. Held HERE, not in the modal, so every re-projection
    // below — the catalog resolve, the trade read — projects over it rather than wiping it.
    const derived = new BulkDerivations()
    const project = (source: BulkTransactionEntry[]) =>
      projectBulkTransaction({
        mode,
        entries: source,
        metadata: this.transactionMetadata,
        values: this.transactionValues,
        derived,
      })

    // One owner of `instance.vm`, for the reason the single dialog states.
    let current = entries
    const render = (source: BulkTransactionEntry[] = current): void => {
      current = source
      instance.vm = project(source)
    }

    render(entries)
    // A refused selection has no grid to fill, so it asks for nothing: no lists to resolve, no
    // derivations to run, no records to read. Otherwise a selection spanning several types resolved
    // the catalogs and read its records for a dialog that only says "pick one type", with the read's
    // loading overlay sitting over the refusal while it ran.
    if (!instance.vm?.blocked) {
      // The lists are one set for the whole grid, so they resolve under the account in focus rather
      // than whichever row happens to be first in a selection that may span accounts.
      this.resolveListValues(accountIdOf(null, this.focusedAccountId()), () =>
        render(),
      )
      this.deriveBulkOnOpen(instance.vm, entries, derived, () => render())
      this.prefillBulkFromRecords(instance, entries, render)
    }

    ref.result.then(
      () => undefined,
      () => undefined,
    )
  }

  /**
   * The bulk grid's opening derivations (PVAL-26685): ENFN Account and General Ledger, which a
   * transaction does not carry in bulk but its account answers.
   *
   * One call per distinct account in the selection, because each answer is that account's own and a
   * `/resolve` call carries a single `accountId`. They go through the derivation seam rather than
   * the opening list call, so the offline flag binds the same canned answers it does for the single
   * dialog. While a call is out its fields are `pending`, which keeps them from holding Save; the
   * grid re-projects on every answer, as it does for the catalogs and the trade read.
   *
   * Only `context` derivations run: a bulk grid has no cell to trigger a field derivation from.
   */
  private deriveBulkOnOpen(
    model: BulkTransactionViewModel | null,
    entries: readonly BulkTransactionEntry[],
    derived: BulkDerivations,
    reproject: () => void,
  ): void {
    if (!model || model.blocked || !model.derivations.length) return
    const asked = [
      ...new Set(entries.map(entry => accountIdOf(entry.row))),
    ].flatMap(account => {
      const requests = bulkOpeningDerivations(model.derivations, account)
      return requests.length ? [{ account, requests }] : []
    })
    if (!asked.length) return

    asked.forEach(({ account, requests }) => derived.begin(account, requests))
    reproject()

    const source = this.injector.get(TransactionDerivationSource)
    asked.forEach(({ account, requests }) =>
      source
        .resolve(
          requests.map(({ keyName, params }) => ({ keyName, params })),
          account,
        )
        .subscribe(results => {
          derived.settle(account, requests, results)
          reproject()
          this.cdr.markForCheck()
        }),
    )
  }

  /**
   * Drop a dialog's spinner and repaint.
   *
   * Called from `finalize`, so it runs on emit, on error and on an empty completion alike. Setting
   * the flag is not enough on its own — the host owns a different change detector from the dialog,
   * and an empty completion arrives without the subscriber work that would otherwise have marked it.
   */
  private clearLoading(instance: { loading: boolean }): void {
    instance.loading = false
    this.cdr.markForCheck()
  }

  /**
   * Freeze a dialog on a failed read.
   *
   * The overlay STAYS, showing the message instead of the spinner, and keeps swallowing clicks —
   * `hs-loader`'s `active` class is bound to `frozen`, which an `errorLabel` holds true. A read
   * that failed must not hand back a half-filled form: the grid-projected values are a fraction of
   * the record, and a user who edited and saved from them would be writing over the rest with
   * whatever the grid happened to carry.
   *
   * The detail goes to `hs-dialog-error-details` below, which is where the status, url and RPS's
   * own rejection body are readable and copyable.
   */
  private freezeOnError(
    instance: {
      errorLabel: string | undefined
      errorHeader: string | undefined
      errorDetails: unknown
    },
    error: unknown,
  ): void {
    instance.errorLabel = TRADE_READ_ERROR_HEADER
    instance.errorHeader = TRADE_READ_ERROR_HEADER
    instance.errorDetails = error
    this.cdr.markForCheck()
  }

  /**
   * Replace the bulk grid's row-projected values with the whole records, once helios answers: the
   * trades behind trade rows, the journal entries behind GL Entry rows.
   *
   * **One call for the whole selection, not one per row.** Both reads take a list of ids and answer
   * documents keyed by `id`, which is exactly the shape this screen needs — a per-row fetch would be
   * N round trips for the one dialog that always has N rows. Rows whose record came back are
   * replaced; rows whose id resolved to nothing keep what the grid gave them, so a partial answer
   * degrades per row instead of failing the dialog.
   *
   * The read is the one the selection's ids call for (`recordSources`). The grid refuses a mixed
   * selection, so there is only ever one; a row that lacks that kind of id is simply not read.
   */
  private prefillBulkFromRecords(
    instance: BulkTransactionModalComponent,
    entries: BulkTransactionEntry[],
    render: (source: BulkTransactionEntry[]) => void,
  ): void {
    const source = this.recordSources.find(candidate =>
      entries.some(entry => idText(entry.row?.fields[candidate.idField])),
    )
    if (!source) return

    const idOf = (entry: BulkTransactionEntry): string =>
      idText(entry.row?.fields[source.idField])

    const ids = entries.map(idOf).filter(Boolean)

    // Every entry of a bulk edit shares one tran category — the grid refuses a mixed selection —
    // so the category is the selection's, read off the first entry that resolved to one.
    const resolved = entries.find(
      entry => entry.resolution.kind === 'tranCategory',
    )?.resolution
    const tranCategory =
      resolved?.kind === 'tranCategory' ? resolved.tranCategory : undefined

    instance.loading = true
    instance.errorLabel = undefined
    instance.errorHeader = undefined
    instance.errorDetails = null

    // One call, so one account: the first row's. helios derives the client from it, so a selection
    // spanning clients is read as the first row's.
    source
      .read(ids, accountIdOf(entries[0]?.row ?? null), tranCategory)
      // See the single dialog's note: the spinner clears however the stream ends.
      .pipe(finalize(() => this.clearLoading(instance)))
      .subscribe(result => {
        // Cleared on every outcome — see the single dialog's note.
        instance.loading = false
        // A failed read FREEZES the dialog rather than revealing a half-filled form — see
        // `freezeOnError`. Nothing below runs, so no partial document is projected over the row.
        if (result.error != null) {
          this.freezeOnError(instance, result.error)
          return
        }

        const documents = result.documents
        if (!documents.size) {
          this.cdr.markForCheck()
          return
        }
        render(
          entries.map(entry => {
            const document = documents.get(idOf(entry))
            return document && entry.row
              ? {
                  ...entry,
                  row: {
                    ...entry.row,
                    fields: { ...entry.row.fields, ...document },
                  },
                }
              : entry
          }),
        )
        this.cdr.markForCheck()
      })
  }

  /**
   * The row's in-session matched counterparts, from a legacy local match group. Fetched flex rows
   * carry no group (`row.adapter.ts`), so in production this is empty and the edit dialog's
   * server-matched counterparts come from `transactions/matches` instead (PVAL-26451).
   */
  private prometheusCounterparts(row: WorkbenchRow): WorkbenchRow[] {
    if (!row.group) return []
    // Every matched counterpart, not just the Prometheus pair: the dialog compares against Custody,
    // Trading AND Clearwater, and filtering Clearwater out here would leave its column permanently
    // absent for a reason that has nothing to do with the record.
    return this.matching
      .groupMembers(row.group)
      .filter(member => member.rowId !== row.rowId && member.origin !== 'api')
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosClientSideRowConfig,
      ...heliosMultiRowSelectionConfig,
      columnDefs: this.colDefs,
      rowData: this.store.rowsForSource(this.sourceKey),
      getRowNodeId: (data: WorkbenchRow) => data.rowId,
      context: this.gridContext,
      // Cell highlighting (right-click -> Highlight Cell) is applied here rather than per column:
      // the columns are catalog-driven, so there is no fixed list to attach it to, and no colDef in
      // this panel sets its own `cellStyle`. Spread first so the shared Helios defaults
      // (width, filterParams, sortable, tooltip) survive — assigning `defaultColDef` wholesale
      // would drop them.
      defaultColDef: {
        ...heliosClientSideRowConfig.defaultColDef,
        cellStyle: (params: CellClassParams) => this.cellStyle(params),
      },
      // `panelSettings` is a Helios-defined iconKey, not one of ag-Grid's built-ins, so the side-bar
      // button has no glyph unless the grid supplies one. Every other Helios grid that exposes
      // Panel Settings does exactly this.
      icons: {
        panelSettings: generateMdiIcon('mdi-cog'),
      },
      // Shown whenever a query comes back empty, and for Clearwater, which has no read endpoint.
      // ag-Grid's default overlay says "No Rows To Show"; the design says "No Data".
      overlayNoRowsTemplate:
        '<span class="wb-no-rows" role="status">No Data</span>',
      statusBar: workbenchStatusBarPanel,
      sideBar: generateHeliosAgGridSidebarFromScratch([
        createPanelSettingsToolPanel({
          controls: [
            {
              label: 'Clear Filters on Load',
              formControl: this.clearFiltersOnAccountLoadToggle,
            },
          ],
        }),
        createWorkbenchColumnManagerToolPanel({
          columnGroups: () => this.colGroups(),
          groupOrder: () => this.colGroupOrder(),
        }),
        createFilterGroupsToolPanel<unknown>(this.filterGroupsToolPanelParams),
      ]),
      isExternalFilterPresent: () => externalFilterActive(this.externalModel()),
      doesExternalFilterPass: (node: RowNode) =>
        node.data
          ? passesExternalFilter(
              node.data as WorkbenchRow,
              this.externalModel(),
              row => this.matching.statusOf(row),
            )
          : true,
      // ag-Grid 26 types `ColDef.filter` as `new () => IFilterComp`, which an Angular filter component
      // can never satisfy, so the Aggregate Info filter is referenced by name and registered here —
      // the same idiom the Northfork filters in `@helios/ag-grid-config` use.
      frameworkComponents: {
        [AGG_INFO_FILTER]: AggInfoColumnFilterComponent,
      },
      getRowClass: (params: RowClassParams) => this.rowClass(params),
      onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
      onSelectionChanged: () => this.onSelectionChanged(),
      onCellClicked: (event: CellClickedEvent) => this.onCellClicked(event),
      onRowDoubleClicked: (event: RowDoubleClickedEvent) =>
        this.onRowDoubleClicked(event),
      // Both route through the same snapshot guard: a change to either is a change to the same payload,
      // and both fire for reasons that leave that payload identical.
      onFilterChanged: () => this.onFilterOrSortChanged(),
      onSortChanged: () => this.onFilterOrSortChanged(),
      onFirstDataRendered: () => this.refreshFilterState(),
      getContextMenuItems: (params: GetContextMenuItemsParams) =>
        this.getContextMenuItems(params),
      ...disableChangeDetectionOnScroll(this.cdr),
    }
  }

  private onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api
    this.columnApi = event.columnApi
    this.store.registerGridApi(this.role, event.api)

    // Selection + clipboard semantics shared with every other Helios grid: click-drag and
    // ctrl-drag over cells select whole rows, ctrl-C copies the selection.
    enableHeliosMultiRowDragSelection(event.api)
    enableHeliosCopyBehavior(event.api)

    // Row and header heights follow the user's density preference (Compact by default) so the
    // workbench matches the other panels instead of using ag-Grid's untuned defaults.
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroy$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, level === DensityLevel.Compact),
      )

    // Register with the shared Filter Modal. Scoped per role so the two grids keep separate
    // filter groups; the modal writes straight into this grid's filter model.
    this.filterModal.registerGrid(
      event.api,
      Transactions2Key,
      `${Transactions2Key}-${this.role}Grid`,
    )

    this.refreshFilterState()
  }

  // ---- selection + related-row tinting --------------------------------------

  private onSelectionChanged(): void {
    const ids = this.selectedRows().map(row => row.rowId)
    this.store.reportSelection(this.role, ids)
  }

  private selectedRows(): WorkbenchRow[] {
    if (!this.gridApi) return []
    return this.gridApi
      .getSelectedNodes()
      .map(node => node.data as WorkbenchRow | undefined)
      .filter((row): row is WorkbenchRow => !!row)
  }

  private rowClass(params: RowClassParams): string[] {
    const row = params.data
    if (!row) return []
    const classes: string[] = []

    // Contextual row labelling (right-click -> Highlight Row). `HighlightingService` keys off
    // `node.id`, which `getRowNodeId` makes the row id, and returns `highlighted <colour>` as one
    // space-separated string — styled globally in `apps/helios/src/styles.scss`, so no per-panel CSS
    // is needed. Its `:before` overlay paints above the related tint below, which is what makes an
    // explicit highlight win visually over an incidental one.
    const highlight = this.highlighting.getRowClass(params.node.id ?? '')
    if (highlight) classes.push(...highlight.split(' '))

    // The cross-grid "related" tint. Selection tinting rides on ag-Grid's own `.ag-row-selected`,
    // because `getRowClass` is not re-evaluated when selection changes — rows would stay untinted
    // while click-dragging until something else forced a redraw.
    // Server truth, not a local group id: the related ids come from the read response's match id columns.
    if (this.relatedRowIds.has(row.rowId)) classes.push('wb-related')

    return classes
  }

  /**
   * The inline style for one cell, carrying its contextual highlight.
   *
   * Keyed by `colDef.field`, which is the same key `HighlightingService.createHighlightMenuItems`
   * records under (`getIdentifiers` reads `column.getColDef().field`) — so a cell highlighted from
   * the menu is the cell that lights up. Returns `{}` for an unhighlighted cell, which ag-Grid
   * treats as no style.
   */
  private cellStyle(params: CellClassParams): CellStyle {
    return this.highlighting.getCellStyle(
      params.node?.id ?? '',
      params.colDef?.field ?? '',
    )
  }

  // ---- match-count cross-filter ---------------------------------------------

  private onCellClicked(event: CellClickedEvent): void {
    if (!event.data) return
    const colId = event.column.getColId()
    const target = this.matchTargetForColId(colId)
    if (!target) return
    const count = Number(event.value ?? 0)
    if (!count) return
    const rowIds = this.matching.matchedRowIds(event.data, target)
    this.store.crossFilterToMatches(this.role, target, rowIds)
  }

  /**
   * Resolve a clicked column to its match target, if any.
   *
   * On the live-API path the match columns are built from the catalog entry and carry the API
   * colIds (`custodyMatches` etc.), so we look those up in MATCH_FIELD_TARGETS. On the
   * computed-fallback path (no focused account / CW / failed fetch) the UI builds them with its own
   * `matchCount:<target>` scheme, so we still honour that prefix.
   */
  private matchTargetForColId(colId: string): MatchTargetKey | undefined {
    const catalogTarget = MATCH_FIELD_TARGETS[colId]
    if (catalogTarget) return catalogTarget
    if (colId.startsWith('matchCount:')) {
      return colId.slice('matchCount:'.length) as MatchTargetKey
    }
    return undefined
  }

  /**
   * Double-clicking an Enfusion row opens the Create/Edit Transaction modal — a net-new entry point
   * (PVAL-26341). Other sources have no curated metadata, so a double-click there does nothing
   * rather than opening an empty dialog.
   */
  private onRowDoubleClicked(event: RowDoubleClickedEvent): void {
    if (this.sourceKey !== 'api') return
    const row = event.data as WorkbenchRow | undefined
    if (row) this.editTransaction(row)
  }

  // ---- context menu ---------------------------------------------------------

  private getContextMenuItems(params: GetContextMenuItemsParams) {
    const scopeRows = getSelectedForContextMenu<WorkbenchRow>(params)
    // Resolved once and shared with `onMatch`, so the rows the menu pre-flighted are exactly the rows
    // the action submits.
    const selectionRows = this.matchScope(scopeRows)
    return buildContextMenuItems({
      sourceKey: this.sourceKey,
      matching: this.matching,
      scopeRows,
      selectionRows,
      onMatch: () => this.match(selectionRows),
      onUnmatch: () => this.unmatch(selectionRows),
      onHold: () =>
        this.holdOrIgnore(FlexTransactionActionType.hold, scopeRows),
      onIgnore: () =>
        this.holdOrIgnore(FlexTransactionActionType.ignore, scopeRows),
      // Local-only, because there is no `unignore` verb to post — see the item's own comment.
      onUnignore: rowIds => this.matching.ignore(rowIds, false),
      onPost: (label, tranCategory) =>
        this.postTransaction(label, scopeRows, tranCategory),
      // The same tree the Create button offers, so the two pickers cannot drift.
      createMenu: this.transactionMetadata.createMenu(),
      onEditTransaction: () => this.editTransactionsBulk(scopeRows),
      highlightItems: this.highlighting.createHighlightMenuItems(params),
      filterOnCell: getFilterOnCellContextMenu(params),
    })
  }

  /**
   * The rows a Match would submit: everything selected across both grids, plus the right-clicked
   * rows. ag-Grid scopes a context menu to the clicked row when it is not part of the selection
   * (`getSelectedForContextMenu`), and the clicked row is unambiguously part of what the user meant.
   *
   * Keyed by origin AND row id, because the two sides' id spaces are independent and can collide —
   * the contract's own sample curl matches id `1316698909` on the Enfusion side to `1316698909` on
   * the Prometheus side.
   *
   * @param scopeRows the right-clicked rows
   * @returns the deduplicated match scope
   */
  private matchScope(scopeRows: WorkbenchRow[]): WorkbenchRow[] {
    const byId = new Map<string, WorkbenchRow>()
    ;[...this.store.allSelectedRows(), ...scopeRows].forEach(row =>
      byId.set(`${row.origin}:${row.rowId}`, row),
    )
    return [...byId.values()]
  }

  /**
   * Match / Match Additional. The request body is identical for both — the server resolves
   * create-vs-add-to-group itself from the ids — so there is one code path and one action type.
   *
   * @param rows the match scope, already pre-flighted by the menu
   */
  private match(rows: WorkbenchRow[]): void {
    const mapping = mapRowsToActionIds(rows)
    this.submitAction(
      FlexTransactionActionType.match,
      {
        enfnTranIds: mapping.enfnTranIds,
        extTranIds: mapping.extTranIds,
      },
      [...mapping.enfnTranIds, ...mapping.extTranIds],
    )
  }

  /**
   * Unmatch, whole-group only.
   *
   * The request carries no group id: the server resolves the given rows to their `matchId`s and
   * dissolves each whole group. Partial unmatch does not exist server-side
   * (`RemoveMatchGroupMembersCommand` is explicitly not wired), so the modal is a confirm, not a
   * picker — dissolving a multi-source group, or several groups at once, with no dialog at all would
   * be a regression in the other direction.
   *
   * Scoped like {@link match}: the selection across BOTH grids plus the right-clicked rows. So when
   * the user has selected matched rows in both tables the body names both id sides, and when they
   * have selected in one it names that one — `ACTION_SIDES` marks both unmatch sides optional, and
   * an empty side is omitted rather than sent as `[]`.
   *
   * Only matched rows contribute: an unmatched row in the selection would resolve to no `matchId`
   * server-side, so submitting its id could only draw a rejection for a row the user was not asking
   * to unmatch.
   *
   * @param scope the unmatch scope, already pre-flighted by the menu
   */
  private unmatch(scope: readonly WorkbenchRow[]): void {
    const rows = scope.filter(row => this.matching.isMatched(row))
    if (!rows.length) return

    const submit = (): void => {
      const mapping = mapRowsToActionIds(rows)
      this.submitAction(
        FlexTransactionActionType.unmatch,
        {
          enfnTranIds: mapping.enfnTranIds,
          extTranIds: mapping.extTranIds,
        },
        rows.map(row => row.rowId),
      )
    }

    const detached = this.detachedSources(rows)
    const groupKeys = this.matching.matchGroupKeysOf(rows)

    // Two independent reasons to stop and ask, either one sufficient:
    //
    //   - the selection spans more than one match group, so one click dissolves N groups. This is
    //     the case `detached` cannot see: with the panel's most common topology — Enfusion rows each
    //     matched to one Custody row — N matched rows produce a single *source* ('cust') but N
    //     separate groups, and gating on the source count alone would take them all apart silently.
    //   - the group reaches more than one other visible source, so more comes apart than the user
    //     has selected.
    //
    // A single group reaching a single other source keeps the previous behaviour of going straight
    // through: there was never a dialog for it, and the confirm exists for the destructive-and-
    // surprising cases, not for every unmatch. The server rejects group merging outright, so a
    // wrongly dissolved grouping may not be rebuildable through the UI at all — which is why the bar
    // for skipping the dialog is this narrow.
    if (groupKeys.length < 2 && detached.length <= 1) {
      submit()
      return
    }

    const ref = this.modal.open(UnmatchModalComponent, {
      size: 'sm',
      centered: true,
    })
    const instance = ref.componentInstance as UnmatchModalComponent
    instance.title =
      rows.length > 1 ? `${rows.length} transactions` : rows[0].sec ?? ''
    instance.sources = detached
    instance.groupCount = groupKeys.length
    ref.result.then(
      (result: UnmatchModalResult) => {
        if (result?.confirmed) submit()
      },
      () => undefined,
    )
  }

  /**
   * Hold / Ignore of External (Prometheus) transactions — PVAL-26309, server side helios !498.
   *
   * Both are External-only, so only `extTranIds` is offered to the builder: `ACTION_SIDES` marks the
   * Enfusion side `unused`, which omits the key from the body rather than sending `[]`, and passing
   * Enfusion ids for these verbs throws. Rows with a synthetic `rowId` are excluded by
   * `mapRowsToActionIds`, and a scope left with no Prometheus id fails in the builder — in-panel —
   * rather than posting a body with nothing to act on.
   *
   * There is deliberately no client-side check of the server's status rules (!498 gates hold on
   * `{UNMATCHED, IGNORED}` and ignore on `{UNMATCHED, HELD}`): the server owns those, and a rejection
   * comes back as a 400 rendered from its `validationId`. That is the NORMAL path in every session
   * after the first — the read projection cannot yet show that a row is already held or ignored, so
   * the user can only find out by asking.
   *
   * @param actionType `hold` or `ignore`
   * @param rows the right-clicked Prometheus rows
   */
  private holdOrIgnore(
    actionType: FlexTransactionActionType,
    rows: readonly WorkbenchRow[],
  ): void {
    const mapping = mapRowsToActionIds(rows)
    this.submitAction(
      actionType,
      { extTranIds: mapping.extTranIds },
      mapping.extTranIds,
    )
  }

  /**
   * Which other sources the group(s) being unmatched span, for the confirm's disclosure.
   *
   * The counts are a MINIMUM, not a determinable figure: the matched ids can name rows outside the
   * loaded page (`PAGE_SIZE` rows) or in a source no grid is currently pointed at, and those members are simply
   * invisible from here. So the modal discloses *which sources come apart*, and the number beside
   * each is "at least this many".
   *
   * @param rows the rows being unmatched
   * @returns one entry per other source with visible members
   */
  private detachedSources(rows: WorkbenchRow[]): UnmatchSourceOption[] {
    const matchedIds = new Set<string>()
    rows.forEach(row =>
      this.matching.matchedIdsOf(row).forEach(id => matchedIds.add(id)),
    )
    if (!matchedIds.size) return []

    const ownOrigins = new Set(rows.map(row => row.origin))
    const counts = new Map<RowOrigin, number>()
    this.store.allLoadedRows().forEach(row => {
      if (!matchedIds.has(row.rowId) || ownOrigins.has(row.origin)) return
      counts.set(row.origin, (counts.get(row.origin) ?? 0) + 1)
    })

    return [...counts.entries()].map<UnmatchSourceOption>(
      ([origin, count]) => ({
        origin,
        label: TARGET_META[origin].label,
        color: TARGET_META[origin].color,
        count,
      }),
    )
  }

  /**
   * Whether a row-update event's ids touch anything this grid is showing.
   *
   * The event's `rowIds` is the cross-feed union of every id in the changed match group — the server
   * sends the same unfiltered set to both feeds' events — so most of it routinely belongs to the
   * sibling grid. An id this grid does not hold is a no-op: the row is outside the loaded page (or in
   * the other feed entirely), so there is nothing on screen to correct.
   *
   * An absent or empty payload is treated as "refresh anyway" rather than "ignore": we cannot rule
   * out a change we were not told the scope of, and a wasted reload is cheaper than a stale grid.
   *
   * @param rowIds the event's affected ids
   * @returns true when a reload could change what this grid renders
   */
  private touchesLoadedRows(rowIds: string[] | undefined): boolean {
    if (!rowIds?.length) return true
    const affected = new Set(rowIds)
    return this.store
      .rowsForSource(this.sourceKey)
      .some(row => affected.has(row.rowId))
  }

  // ---- action submission ----------------------------------------------------

  /**
   * Post one action and reconcile the grid with the outcome.
   *
   * The write path is asynchronous — the 200 carries only a command id, and the match itself is
   * applied by perseus and re-hydrated by flex-transactions afterwards — so success here means
   * "accepted", not "applied". Until the flex row-update events are deployed the interim seam
   * applies the expected outcome locally and then refetches to confirm it, reverting and disclosing
   * rather than leaving a lie on screen if the refetch disagrees.
   *
   * {@link OPTIMISTIC_STATUS} verbs (`hold` / `ignore`) are the exception at both ends: their
   * interim apply is a flat status, and they do not refetch at all, because the flex read projection
   * does not carry that status yet. That is also why they are the verbs carrying a
   * {@link FLEX_ACTION_SUCCESS_NOTE}: acceptance is all the user is ever going to see.
   *
   * @param actionType the verb
   * @param ids candidate id sets by side
   * @param optimisticRowIds the rows whose local state the interim apply should touch
   */
  private submitAction(
    actionType: FlexTransactionActionType,
    ids: ActionIdSets,
    optimisticRowIds: string[],
  ): void {
    this.actionError = null
    this.actionNote = null
    this.actionPending = false
    this.actionPendingNeedsRun = false

    let request
    try {
      request = buildFlexTransactionActionRequest(actionType, ids)
    } catch (err: unknown) {
      // The menu's pre-flight checks should have caught this. If one is missing, fail visibly rather
      // than posting a body we already know the server will reject.
      this.actionError =
        err instanceof Error ? err.message : 'Unable to submit this action.'
      this.cdr.markForCheck()
      return
    }

    // Read BEFORE the optimistic apply, which makes every actioned row read as matched. A match none
    // of whose rows is already matched creates a group, and the server elects `FLEX_MATCH` for it —
    // which is the one outcome that cannot appear until the next Run.
    const actioned = new Set(optimisticRowIds)
    const createsGroup =
      actionType === FlexTransactionActionType.match &&
      !this.store
        .allLoadedRows()
        .some(row => actioned.has(row.rowId) && this.matching.isMatched(row))

    // INTERIM (PVAL-26130 CHANGE 6). Delete this optimistic block, and `reconcile` with it, once the
    // backend's match-changed event is live — the event-driven `rowsRefresh$` is the real answer.
    //
    // Dispatch is per verb rather than a two-arm if/else: `hold` / `ignore` change a row's status
    // without touching match membership, and the else arm here writes `matched`.
    const snapshot: MatchStateSnapshot = this.matching.snapshotMatchState()
    const optimisticStatus = OPTIMISTIC_STATUS[actionType]
    if (optimisticStatus) {
      this.matching.applyOptimisticStatus(optimisticRowIds, optimisticStatus)
    } else if (actionType === FlexTransactionActionType.unmatch) {
      this.matching.applyOptimisticUnmatch(optimisticRowIds)
    } else {
      this.matching.applyOptimisticMatch(optimisticRowIds)
    }

    this.actions
      .submit(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // The actioned rows are about to re-render with new Status and counts, so a stale
          // selection would leave the menu offering an action against a view of them that no longer
          // holds.
          this.store.clearSelection()

          // Disclosure on the SUCCESS path, not only in the rejection copy. A verb with no note
          // leaves this null and says nothing.
          this.actionNote = FLEX_ACTION_SUCCESS_NOTE[actionType] ?? null

          // Hold and Ignore deliberately do NOT reconcile. The server's state IS durable — !498
          // writes an `ExtTranStatus` through `HOLD_EXT_TRAN` / `IGNORE_EXT_TRAN` — but the flex
          // READ projection does not carry it yet (`FlexExternalTransactionRow.matchStatus` is
          // hardcoded `"UNMATCHED"`), and a refetch runs `matching.init`, which clears the overlay.
          // Reconciling would therefore replace a correct view with a false `unmatched`.
          // RE-ENABLE the reconcile for these verbs as soon as the read path carries the status —
          // at that point the refetch becomes the thing that CONFIRMS the write, as it is for
          // match / unmatch. Until then the status is visible only until the next fetch, and is
          // reversible only from the Transactions 1.0 External Transactions panel — which is what
          // the note above discloses.
          if (optimisticStatus) {
            this.cdr.markForCheck()
            return
          }

          this.reconcile(actionType, optimisticRowIds, 1, createsGroup)
        },
        error: (err: unknown) => {
          // Rejected outright: undo the optimistic apply immediately rather than waiting for a
          // refetch to contradict it.
          this.matching.restoreMatchState(snapshot)
          this.actionError = this.actionErrorMessage(err)
          this.cdr.markForCheck()
        },
      })
  }

  /**
   * Refetch this grid's rows and check whether the accepted action has actually landed.
   *
   * Bounded rather than polled: each attempt is a full page reload, so this makes at most
   * {@link RECONCILE_ATTEMPTS} of them and exits early the moment server truth agrees. The refetch
   * itself is what reverts the optimistic view — the index is rebuilt from the response — so an
   * action that has not landed yet leaves the grid showing the server's state plus a disclosure,
   * never a fabricated one.
   *
   * A create is expected NOT to agree, however many attempts it makes: portfolio applies nothing for
   * a `FLEX_MATCH` in-session, so the rows stay unmatched until the next Run. `createsGroup` is
   * carried through purely so the disclosure at the end says that, instead of promising an update
   * this session will never deliver.
   *
   * @param actionType the verb that was submitted
   * @param rowIds the rows it was submitted for
   * @param attempt 1-based attempt number
   * @param createsGroup whether this action creates a new match group
   */
  private reconcile(
    actionType: FlexTransactionActionType,
    rowIds: string[],
    attempt: number,
    createsGroup: boolean,
  ): void {
    of(undefined)
      .pipe(
        delay(WorkbenchGridComponent.RECONCILE_DELAY_MS),
        switchMap(() => this.loadRows()),
        takeUntil(this.destroy$),
      )
      .subscribe(rows => {
        this.applyRowsPreservingState(rows)

        // A create cannot land in-session, so retrying is a guaranteed-useless page reload. One
        // refetch is still worth making: it is what replaces the optimistic view with server truth.
        if (createsGroup) {
          this.actionPending = true
          this.actionPendingNeedsRun = true
          this.cdr.markForCheck()
          return
        }

        if (this.outcomeApplied(actionType, rowIds)) {
          this.actionPending = false
          this.actionPendingNeedsRun = false
          this.cdr.markForCheck()
          return
        }

        if (attempt >= WorkbenchGridComponent.RECONCILE_ATTEMPTS) {
          // Accepted but not visible. For a create that is the designed outcome, not a delay; for
          // anything else the command may still land. Either way, say which instead of implying it
          // failed or pretending it succeeded.
          this.actionPending = true
          this.actionPendingNeedsRun = createsGroup
          this.cdr.markForCheck()
          return
        }

        this.reconcile(actionType, rowIds, attempt + 1, createsGroup)
      })
  }

  /**
   * Whether the freshly-fetched rows of THIS grid's source reflect the submitted action.
   *
   * Scoped to this source on purpose: the sibling grid reloads its own source on its own schedule, so
   * judging the outcome on rows this grid did not just refetch would report a false negative.
   *
   * For a match, "is it matched?" is deliberately NOT the test — a Match Additional's rows were
   * already matched before the action, so that would pass on the first attempt without the new
   * membership having landed. What proves the action applied is the new relationship: each actioned
   * row of this source must now name at least one of the OTHER actioned rows among its matched ids.
   *
   * @param actionType the verb that was submitted
   * @param rowIds the rows it was submitted for
   * @returns true when every one of this source's actioned rows now reads as the action intended
   */
  private outcomeApplied(
    actionType: FlexTransactionActionType,
    rowIds: string[],
  ): boolean {
    const ids = new Set(rowIds)
    const rows = this.store
      .rowsForSource(this.sourceKey)
      .filter(row => ids.has(row.rowId))
    if (!rows.length) return false

    if (actionType === FlexTransactionActionType.unmatch) {
      return rows.every(row => !this.matching.isMatched(row))
    }

    return rows.every(row => {
      const matchedIds = new Set(this.matching.matchedIdsOf(row))
      return rowIds.some(id => id !== row.rowId && matchedIds.has(id))
    })
  }

  /**
   * The message for a failed action.
   *
   * A 400 from this endpoint carries a JSON array of `{ validationId, message }`, and the copy is
   * resolved from `validationId` FIRST — deliberately, not as a fallback. The server's own `message`
   * is nulled outside lower environments (`ReactiveGlobalExceptionMapper`), which is exactly why the
   * stable code is carried in the body as well; rendering the prose would show a blank in
   * production. The prose is only used when the code is one we have no copy for, so a code added
   * server-side still surfaces something useful rather than a generic failure.
   *
   * Anything that is not that array — a 409, a 5xx, a transport failure — falls back to the
   * StandardApiError envelope, then to generic copy.
   *
   * @param err the error the interceptor rethrew — via the bypass list for a 400/409, via
   *            `HANDLE_ERROR_AND_THROW` for anything else
   * @returns the message to render
   */
  private actionErrorMessage(err: unknown): string {
    const fallback = 'This action could not be completed. Please try again.'
    if (!(err instanceof HttpErrorResponse)) return fallback

    const body: unknown = err.error

    const validations = readValidations(body)
    if (validations.length) {
      const messages = validations.map(
        validation =>
          FLEX_ACTION_VALIDATION_COPY[validation.validationId] ??
          validation.message?.trim() ??
          fallback,
      )
      return [...new Set(messages)].join(' ')
    }

    if (typeof body === 'string' && body.trim()) return body.trim()

    const envelope = body as ReportableErrorEnvelope | null | undefined
    return envelope?.displayMessage ?? envelope?.errorMessage ?? fallback
  }

  /** Dismiss the in-panel action message. */
  dismissActionMessage(): void {
    this.actionError = null
    this.actionNote = null
    this.actionPending = false
    this.actionPendingNeedsRun = false
  }

  // ---- filters --------------------------------------------------------------

  private externalModel(): WorkbenchExternalFilterModel {
    return {
      crossFilterIds: this.crossFilterIds,
      status: this.source?.hasStatusFilter ? this.statusFilterState() : null,
      quick: this.isClearwater ? this.quickFilterState() : null,
      fxCurrencies: this.isClearwater ? this.selectedFxCurrencies() : null,
    }
  }

  private statusFilterState(): StatusFilterState {
    return {
      matched: this.includeMatchedFormControl.value === true,
      unmatched: this.includeUnmatchedFormControl.value === true,
      ignored: this.includeIgnoredFormControl.value === true,
    }
  }

  private quickFilterState(): QuickFilterState {
    return {
      hideFuture: this.hideFuturePostDatesToggle.value === true,
      postToday: this.filterPostDateTodayToggle.value === true,
      cleared: this.filterClearedToggle.value === true,
      uncleared: this.filterUnclearedToggle.value === true,
    }
  }

  private selectedFxCurrencies(): string[] {
    return (this.fxCurrencyFilter.value as string[] | null) ?? []
  }

  onExternalFilterChanged(): void {
    this.gridApi?.onFilterChanged()
    this.refreshFilterState()
  }

  /**
   * The toolbar search box changed (already debounced).
   *
   * On a source with a search column (see `grid/filters/search-filter.ts`) the box is part of the
   * request payload, so a change goes through the same snapshot guard as a column filter and costs
   * exactly one request. It does NOT also filter the loaded rows: the server's answer is the result.
   * A source without one (Clearwater, which has no read endpoint) keeps the client-side quick filter.
   */
  onSearchChanged(): void {
    if (SEARCH_FIELD[this.sourceKey]) {
      this.gridApi?.setQuickFilter('')
      this.maybeRefetch()
    } else {
      this.gridApi?.setQuickFilter(this.quickSearch.value ?? '')
    }
    this.refreshFilterState()
  }

  /** What the search box says it filters, per source. */
  get searchPlaceholder(): string {
    return SEARCH_PLACEHOLDER[this.sourceKey] ?? 'Search'
  }

  /**
   * The search box's contribution to the request, or null when it is empty, disabled, or the source
   * has no search column.
   */
  private searchConstraint(): SearchConstraint | null {
    const field = SEARCH_FIELD[this.sourceKey]
    const value = (this.quickSearch.value ?? '').trim()
    if (!field || !value || this.quickSearch.disabled) return null
    return { field, value }
  }

  /**
   * Enable the search box only when this source's catalog carries its search column.
   *
   * `instrumentId` is a per-client `tran_data` key, so an Enfusion client whose metadata lacks it has
   * nothing to search; a live box there would send nothing (the request builder drops an uncatalogued
   * field) and look broken. Disabled, it says so instead. Silent, so it costs no refetch on its own.
   */
  private updateSearchAvailability(): void {
    const field = SEARCH_FIELD[this.sourceKey]
    const available =
      !field || this.catalogColumns.some(column => column.field === field)
    if (available) this.quickSearch.enable({ emitEvent: false })
    else this.quickSearch.disable({ emitEvent: false })
  }

  /**
   * Clear every filter surface, client and server.
   *
   * `setFilterModel(null)` empties the column filters, `resetToolbar` puts the status checkboxes back
   * to Unmatched-only and the search box is emptied, which together reset the SERVER filter too:
   * `onFilterChanged` then sees a payload of just the default `matchStatus` constraint, which differs
   * from the snapshot, so exactly one refetch is issued (§7).
   */
  clearAll(): void {
    this.resetToolbar()
    this.quickSearch.reset('', { emitEvent: false })
    this.gridApi?.setQuickFilter('')
    this.gridApi?.setFilterModel(null)
    this.store.clearCrossFilter(this.role)
    this.gridApi?.onFilterChanged()
    this.refreshFilterState()
  }

  /**
   * Return every toolbar filter to its default. Silent so a reset triggers a single grid refresh
   * from the caller rather than one per control.
   */
  private resetToolbar(): void {
    const silent = { emitEvent: false }
    this.includeUnmatchedFormControl.setValue(true, silent)
    this.includeIgnoredFormControl.setValue(false, silent)
    this.includeMatchedFormControl.setValue(false, silent)
    this.hideFuturePostDatesToggle.setValue(false, silent)
    this.filterPostDateTodayToggle.setValue(false, silent)
    this.filterClearedToggle.setValue(false, silent)
    this.filterUnclearedToggle.setValue(false, silent)
    this.fxCurrencyFilter.setValue([], silent)
  }

  /** Keep the applied-filter bubble and the Clear-all button in step with the live filter state. */
  private refreshFilterState(): void {
    this.filterCount = Object.keys(this.gridApi?.getFilterModel() ?? {}).length
    this.clearAllFiltersEnabled =
      this.filterCount > 0 ||
      !!this.quickSearch.value ||
      this.crossFilterIds != null ||
      this.isStatusFilterModified() ||
      this.isQuickFilterModified() ||
      this.selectedFxCurrencies().length > 0
    this.cdr.markForCheck()
  }

  private isStatusFilterModified(): boolean {
    const status = this.statusFilterState()
    return !status.unmatched || status.ignored || status.matched
  }

  private isQuickFilterModified(): boolean {
    const quick = this.quickFilterState()
    return (
      quick.hideFuture || quick.postToday || quick.cleared || quick.uncleared
    )
  }

  private deriveFxCodes(): string[] {
    const codes = new Set<string>()
    this.store.rowsForSource('cw').forEach(row => {
      const code = row.fields['currencyCode']
      if (typeof code === 'string') codes.add(code)
    })
    // localeCompare rather than the default sort, which orders by UTF-16 code unit.
    return [...codes].sort((a, b) => a.localeCompare(b))
  }

  // ---- toolbar actions ------------------------------------------------------

  openFilters(): void {
    void this.filterModal.launchFiltersModal()
  }

  exportToExcel(): void {
    this.gridApi?.exportDataAsCsv({
      fileName: `transactions-2-${this.sourceKey}.csv`,
      // A TEXT_LIST cell (the match id lists) holds an array; join it the way the cell shows it
      // rather than letting the CSV writer stringify the array. Every other value is left as is.
      processCellCallback: params =>
        Array.isArray(params.value) ? joinList(params.value) : params.value,
    })
  }
}
